import { useState, useMemo, useEffect, useRef } from "react";
import * as XLSX from "xlsx";
import TenderPeriodReport from "../../components/tenderreport/Tenderreport";
import { getAuthHeaders } from "../../config/axios";

/* ══════════════════════════════════════════════════════
   DESIGN TOKENS
══════════════════════════════════════════════════════ */
const C = {
    p900: "#0C2340", p800: "#103761", p700: "#155285", p600: "#1565a8",
    p500: "#1176d3", p400: "#2f8ae0", p300: "#5ca5e8", p200: "#93c5f4", p100: "#c8e1fa", p50: "#ebf4fd",
    r500: "#1aaddb", r100: "#d0f0fa",
    l500: "#2952cc",
    n900: "#0f1c2e", n800: "#1a2d45", n700: "#2d4263", n600: "#4a6080",
    n500: "#647d9e", n400: "#8fa4be", n300: "#b8cde0", n200: "#d9e7f3", n150: "#edf4fb", n100: "#f4f8fd", white: "#FFFFFF",
    ok: "#0a6c4e", okBg: "#edfaf5", okBd: "#6eddb8",
    warn: "#7a4a00", warnBg: "#fff8e6", warnBd: "#ffd47a",
    err: "#b01c1c", errBg: "#fff0f0", errBd: "#f5a3a3",
    sh1: "0 1px 3px rgba(17,118,211,0.08)", sh2: "0 4px 12px rgba(17,118,211,0.10)",
};
const PAL = ["#1176d3", "#1aaddb", "#2952cc", "#5ca5e8", "#155285", "#0f8ebd", "#1a3fa8", "#2f8ae0", "#5ecbeb", "#6b8fe8", "#1565a8", "#93c5f4", "#e09c13", "#0a6c4e", "#b01c1c", "#8B5CF6", "#f97316", "#06b6d4", "#84cc16", "#ec4899"];
const SEC_COL = { "Highway": "#1176d3", "Railway": "#1aaddb", "Metro": "#2952cc", "Water": "#5ca5e8", "Ropeway": "#0f8ebd", "Building": "#155285", "Other": "#647d9e" };

/* ══════════════════════════════════════════════════════
   VALUE-FIELD RESOLUTION  (FIX #2)
   The API filters tenders by the "_value" representation
   (e.g. tender_type_value, sector_value), which can differ
   from the internal "code" used as analytics dictionary
   keys (e.g. code "IE_OM" -> value "IEO&M"). Sending the
   code instead of the value to the API silently returns
   zero/incorrect results.

   We LEARN the real code -> value mapping from every
   tender object we ever load (100% accurate, since both
   fields are present on every tender), and fall back to a
   small seed table for codes we haven't seen loaded yet.
══════════════════════════════════════════════════════ */
const TYPE_VALUE_SEED = {
    IE_OM: "IEO&M",
    AE_OM: "AEO&M", // best-effort guess by the same pattern as IE_OM — verify against real data
    Supervision_OM: "Supervision_O&M",
    Fesibility_Cum_DPR: "FesibilityCumDPR",
};
const TYPE_VALUE_MAP = { ...TYPE_VALUE_SEED };
const SECTOR_VALUE_MAP = {};

function learnValueMaps(tenders) {
    (tenders || []).forEach(t => {
        if (t.tender_type && t.tender_type_value && !TYPE_VALUE_MAP[t.tender_type]) {
            TYPE_VALUE_MAP[t.tender_type] = t.tender_type_value;
        }
        if (t.sector && t.sector_value && !SECTOR_VALUE_MAP[t.sector]) {
            SECTOR_VALUE_MAP[t.sector] = t.sector_value;
        }
    });
}
function resolveTypeValue(code) { return code ? (TYPE_VALUE_MAP[code] || code) : code; }
function resolveSectorValue(code) { return code ? (SECTOR_VALUE_MAP[code] || code) : code; }

/* ══════════════════════════════════════════════════════
   EMD FILTER RESOLUTION  (FIX #3)
   The EMD donut shows display labels ("No EMD", "< 1L"...)
   but the API expects enum-style wire values (mirrors the
   manual EMD buttons already used in the Financial Open
   tab). Clicking the donut previously sent the raw label,
   which the API didn't recognise -> empty result -> table
   looked "hidden". We translate label -> wire value before
   the request goes out. NOTE: the last two buckets
   (5-20L / >20L) aren't used anywhere else in this file as
   a confirmed wire value — adjust if your backend differs.
══════════════════════════════════════════════════════ */
const EMD_FILTER_MAP = {
    "No EMD": "not_required",
    "< 1L": "required_less_1lakh",
    "1-5L": "required_1_to_5_lakh",
    "5-20L": "required_5_to_20_lakh",
    "> 20L": "required_above_20_lakh",
};
function resolveEmdValue(label) { return label ? (EMD_FILTER_MAP[label] || label) : label; }

/* ══════════════════════════════════════════════════════
   TL FILTER  (FIX #5)
   Query-param name the backend expects for "filter by
   assigned TL" isn't shown in the sample API responses we
   have. "tl" follows the same short-key convention as
   state/client/sector. Change this ONE constant if your
   backend uses something else (e.g. "assigned_tl").
══════════════════════════════════════════════════════ */
const TL_QUERY_PARAM = "tl";

/* ══════════════════════════════════════════════════════
   EMD LIFECYCLE STAGES  (NEW — EMD Tracker tab)
   The EMD Tracker hits the same dashboard endpoint but
   WITHOUT a `tab` param. The response it returns has a
   completely different shape from the bid-pipeline tabs:
   instead of `funnel.tab1_to_tab4` etc. it returns
   `funnel.upcoming_to_created`, `created_to_submitted`,
   `submitted_to_matured`, `matured_to_collected`, and a
   `stage_analytics` object keyed by these same EMD stage
   names (not bid-pipeline tab numbers). Each tender also
   carries its EMD stage on `_stage`. This is a genuinely
   separate lifecycle (money/instrument tracking) from the
   bid-decision pipeline the other 6 tabs walk through,
   which is why this tab is visually called out below.
══════════════════════════════════════════════════════ */
const EMD_STAGES = [
    { key: "upcoming", label: "Upcoming" },
    { key: "created", label: "Created" },
    { key: "submitted", label: "Submitted" },
    { key: "bg_created", label: "BG Created" },
    { key: "matured_pending", label: "Matured (Pending)" },
    { key: "collected", label: "Collected" },
];

/* ══════════════════════════════════════════════════════
   API
   FIX: encodeType uses encodeURIComponent so spaces →
   %20 and & → %26. No raw underscores ever sent.
══════════════════════════════════════════════════════ */
const BASE = "https://bd.aimantra.info";
const API_PATH = "/siteproject/dashboardtender/";
// EMD Tracker tab hits a DIFFERENT endpoint than every other tab, even
// though it reuses the exact same query-string filters (date range,
// sector, client, state, etc.) built by fetchDashboard below.
const EMD_API_PATH = "/siteproject/emd-dashboard/";

function encodeType(val) {
    if (!val) return null;
    const resolved = resolveTypeValue(val);
    return encodeURIComponent(resolved);   // spaces→%20, &→%26
}

async function fetchDashboard(params = {}, apiPath = API_PATH) {
    const p = { ...params, trend: "true" };

    if (p.filter && p.filter !== "all") {
        p.date_range = p.filter;
    } else if (p.filter === "all") {
        p.date_range = "all";
    }

    delete p.filter;

    const parts = [];

    // 1. Sector first — resolved to sector_value (FIX #2)
    if (p.sector && p.sector !== "all") {
        parts.push(`sector=${encodeURIComponent(resolveSectorValue(p.sector))}`);
    }

    // 2. Tender Type second — resolved to tender_type_value (FIX #2)
    if (p.tender_type && p.tender_type !== "all") {
        parts.push(`tender_type=${encodeType(p.tender_type)}`);
    }

    // 3. State
    if (p.state && p.state !== "all") {
        parts.push(`state=${encodeURIComponent(p.state)}`);
    }

    // 4. Client
    if (p.client && p.client !== "all") {
        parts.push(`client=${encodeURIComponent(p.client)}`);
    }

    // 5. Remaining params
    Object.entries(p).forEach(([k, v]) => {
        if (
            v == null ||
            v === "" ||
            v === "all" ||
            ["sector", "tender_type", "state", "client"].includes(k)
        ) {
            return;
        }

        // EMD donut filter resolved to wire value (FIX #3). Safe no-op
        // for values that are already wire values (e.g. from the manual
        // EMD buttons in the Financial Open tab).
        const value = k === "emd_filter" ? resolveEmdValue(v) : v;

        parts.push(
            `${encodeURIComponent(k)}=${encodeURIComponent(value)}`
        );
    });

    const url = `${BASE}${apiPath}${parts.length ? "?" + parts.join("&") : ""
        }`;

    console.log("API URL:", url);

    try {
        const res = await fetch(url, {
            signal: AbortSignal.timeout(12000),
            headers: getAuthHeaders(),
        });

        if (res.ok) {
            return await res.json();
        }

        console.warn("API", res.status, url);
    } catch (e) {
        console.warn("fetch", e.message, url);
    }

    return null;
}

/* ══════════════════════════════════════════════════════
   FIX #1: single hook, with a `skip` flag so we never
   fire a request that would duplicate another in-flight
   request (see useUnfilteredTrend below).
══════════════════════════════════════════════════════ */
function useDashboard(params, skip = false, apiPath = API_PATH) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(!skip);
    const [error, setError] = useState(false);
    const key = JSON.stringify(params) + "|" + skip + "|" + apiPath;
    useEffect(() => {
        if (skip) {
            setData(null);
            setLoading(false);
            setError(false);
            return;
        }
        let dead = false;
        setLoading(true); setError(false);
        fetchDashboard(params, apiPath).then(d => {
            if (!dead) {
                setData(d);
                setLoading(false);
                if (!d) setError(true);
                else learnValueMaps(d.tenders);
            }
        }).catch(() => { if (!dead) { setLoading(false); setError(true); } });
        return () => { dead = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [key]);
    return { data, loading, error };
}

/* FIX #1: this used to ALWAYS fire alongside the tab's main
   request. When no sector/type/client/state/tl filter is
   active, this request is byte-for-byte identical to the
   main request (both reduce to ?trend=true&date_range=...&tab=...),
   which is exactly the duplicate calls seen in the network
   tab. We now only fire it when it would actually return
   different (unfiltered) data than the main, filtered call. */
function useUnfilteredTrend(tab, dateFilter, skip) {
    const params = { filter: dateFilter, tab, trend: "true" };
    // intentionally omit sector, tender_type, client, state, tl
    const { data, loading } = useDashboard(params, skip);
    return { trend: data?.trend_overview || null, loading };
}

/* ══════════════════════════════════════════════════════
   FIX #4: EXPORT TO EXCEL
   Fetches every page that matches the tab's current
   filters (ignoring the on-screen client-side pagination)
   and writes one .xlsx file with all matching tenders.
   Requires the `xlsx` package: npm install xlsx
══════════════════════════════════════════════════════ */
async function fetchAllTenders(params, onProgress, apiPath = API_PATH) {
    let page = 1;
    let all = [];
    let totalPages = 1;
    const HARD_CAP = 200; // safety guard against runaway loops
    while (page <= HARD_CAP) {
        const d = await fetchDashboard({ ...params, page }, apiPath);
        if (!d) break;
        all = all.concat(d.tenders || []);
        totalPages = d.pagination?.total_pages || 1;
        onProgress?.(page, totalPages, all.length);
        if (!d.pagination?.has_next || page >= totalPages) break;
        page++;
    }
    return all;
}

function tendersToExportRows(tenders) {
    return tenders.map(t => ({
        "Tender ID": t.tender_id,
        "Tender Name": t.tender_name,
        "Client": t.client,
        "Sector": t.sector_value || t.sector,
        "Type": t.tender_type_value || t.tender_type,
        "State": t.state,
        "Submission Date": t.submission_date ? t.submission_date.slice(0, 10) : "",
        "Days Left": t.days_left,
        "Cost Amount": t.cost_amount ?? t.cost_of_rfp ?? "",
        "EMD Amount": t.emd_amount,
        "Consultancy Fees %": t.consultancy_fees ?? "",
        "Participating As": t.participating_as || "",
        "Lead Partner": t.lead_partner || "",
        "Tech Score": t.tech_score || "",
        "Responsive Status": t.responsive_status || "",
        "Finalized": t.is_finalized ? "Yes" : "No",
        "We Are L1": t.are_we_l1 ? "Yes" : "No",
        "Assigned TLs": t.tl_details?.assigned_tls_names || "",
        "Main TL": t.tl_details?.main_tl_names || "",
        "Stage": t._stage || "",
    }));
}

function downloadTendersExcel(tenders, filename) {
    const rows = tendersToExportRows(tenders);
    const ws = XLSX.utils.json_to_sheet(rows);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Tenders");
    XLSX.writeFile(wb, filename);
}




/* ══════════════════════════════════════════════════════════════
   TECHNICAL RESULT ANALYTICS — DEEP DIVE
   ─────────────────────────────────────────────────────────────
   Renders the FULL `technical_result_analytics` object that the
   dashboard API returns (it's present on every dashboard call,
   regardless of which `tab` you request — you can see it sitting
   alongside `funnel` / `tab1_analytics` in any response).

   WHERE THIS GOES
   Paste this whole block into your existing TenderDashboard.jsx,
   anywhere below `TechResultAnalyticsTab` (or above — order doesn't
   matter, just keep it top-level). It only uses things already
   defined in your file — Card, Stat, HBar, ProgBar, I, C, PAL,
   Empty, useState, useEffect — so there is nothing new to import.

   HOW TO WIRE IT IN
   Inside `TechResultAnalyticsTab`, right after the <ClientStateRow>
   line and before the <TTable> line, add:

       <TechAnalyticsDeepDive TA={data?.technical_result_analytics} />

   That's it — `data` is already being fetched by that tab's
   `useDashboard(params)` call, and `technical_result_analytics`
   just rides along in the same response.
══════════════════════════════════════════════════════════════ */

/* ---------- small formatting helpers ---------- */
function fmtN(v, d = 1) {
    if (v === null || v === undefined || Number.isNaN(v)) return "—";
    const s = Number(v).toFixed(d);
    return d > 0 ? s.replace(/\.0+$/, "") : s;
}
function fmtPctV(v, d = 1) {
    if (v === null || v === undefined || Number.isNaN(v)) return "—";
    return `${Number(v).toFixed(d)}%`;
}
function ciText(lo, hi, d = 1) {
    if (lo == null || hi == null) return null;
    return `${fmtN(lo, d)}–${fmtN(hi, d)}%`;
}
function truncate(s, n) {
    if (!s) return "";
    return s.length > n ? `${s.slice(0, n)}…` : s;
}

/* ---------- a Stat card for a {rate_pct, ci95_low_pct, ci95_high_pct, trials, successes} probability object ---------- */
function ProbStat({ icon, label, prob, color = C.p500, bg = C.p50 }) {
    if (!prob || !prob.trials || prob.rate_pct == null) {
        return <Stat icon={icon} label={label} value="—" color={C.n400} bg={C.n150} />;
    }
    const ci = ciText(prob.ci95_low_pct, prob.ci95_high_pct);
    return (
        <Stat
            icon={icon}
            label={label}
            value={fmtPctV(prob.rate_pct)}
            sub={ci ? `CI ${ci} · n=${prob.trials}` : `n=${prob.trials}`}
            color={color}
            bg={bg}
        />
    );
}

/* ---------- a compact grid rendering a {count,mean,median,std_dev,min,max,p25,p75,cv_pct} distribution ---------- */
function DistTable({ title, dist, unit = "" }) {
    if (!dist || !dist.count) return <Empty msg="No data." />;
    const rows = [
        ["Mean", dist.mean], ["Median", dist.median],
        ["Std Dev", dist.std_dev], ["Min", dist.min],
        ["Max", dist.max], ["P25", dist.p25],
        ["P75", dist.p75], ["CV %", dist.cv_pct],
    ];
    return (
        <div>
            {title && (
                <div style={{ fontSize: 9.5, fontWeight: 700, color: C.n700, marginBottom: 6 }}>
                    {title} <span style={{ fontWeight: 400, color: C.n400 }}>(n={dist.count})</span>
                </div>
            )}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 6 }}>
                {rows.map(([k, v]) => (
                    <div key={k} style={{ background: C.n100, border: `1px solid ${C.n200}`, borderRadius: 6, padding: "5px 4px", textAlign: "center" }}>
                        <div style={{ fontSize: 11.5, fontWeight: 800, color: C.n800 }}>{fmtN(v)}{v != null ? unit : ""}</div>
                        <div style={{ fontSize: 7, color: C.n500, textTransform: "uppercase", letterSpacing: .3 }}>{k}</div>
                    </div>
                ))}
            </div>
        </div>
    );
}

/* ---------- generic compact ranked table ---------- */
function RankTable({ rows, columns, keyField = "key" }) {
    if (!rows || !rows.length) return <Empty msg="No data." />;
    return (
        <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                    <tr style={{ background: C.n150 }}>
                        {columns.map(c => (
                            <th key={c.header} style={{ padding: "5px 8px", textAlign: c.align || "left", fontSize: 8.5, fontWeight: 700, color: C.n600, borderBottom: `1px solid ${C.n200}`, whiteSpace: "nowrap" }}>
                                {c.header}
                            </th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    {rows.map((r, i) => (
                        <tr key={r[keyField] ?? i} style={{ borderBottom: `1px solid ${C.n200}`, background: i % 2 === 0 ? C.n100 : C.white }}>
                            {columns.map(c => (
                                <td key={c.header} style={{ padding: "5px 8px", fontSize: 9, color: C.n700, textAlign: c.align || "left", whiteSpace: c.wrap ? "normal" : "nowrap" }}>
                                    {c.render ? c.render(r) : (r[c.field] ?? "—")}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/* ---------- tabbed segment explorer (sector / type / client / state / participating-as / lead / jv) ---------- */
const SEGMENT_LABELS = {
    sector_wise: "Sector", tender_type_wise: "Tender Type", client_wise: "Client",
    state_wise: "State", participating_as_wise: "Participating As",
    lead_partner_wise: "Lead Partner", jv_partner_wise: "JV Partner",
};
function SegmentExplorer({ segments }) {
    const keys = Object.keys(segments || {}).filter(k => (segments[k] || []).length);
    const [sel, setSel] = useState(keys[0] || null);
    useEffect(() => { if ((!sel || !keys.includes(sel)) && keys.length) setSel(keys[0]); }, [keys, sel]);
    if (!keys.length) return <Empty msg="No segment data." />;
    const rows = (segments[sel] || []).slice().sort((a, b) => (b.tenders || 0) - (a.tenders || 0));
    return (
        <div>
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 9 }}>
                {keys.map(k => (
                    <button key={k} onClick={() => setSel(k)}
                        style={{ padding: "4px 10px", border: `1px solid ${sel === k ? C.p400 : C.n200}`, borderRadius: 6, background: sel === k ? C.p500 : "transparent", color: sel === k ? C.white : C.n600, fontSize: 9.5, fontWeight: sel === k ? 700 : 400, cursor: "pointer", transition: "all .12s" }}>
                        {SEGMENT_LABELS[k] || k}
                    </button>
                ))}
            </div>
            <RankTable
                rows={rows}
                keyField="segment"
                columns={[
                    { header: SEGMENT_LABELS[sel] || "Segment", field: "segment", wrap: true },
                    { header: "Tenders", field: "tenders", align: "right" },
                    { header: "Decided", field: "decided", align: "right" },
                    { header: "Qual. Rate", align: "right", render: r => r.qualification_rate_pct == null ? "—" : fmtPctV(r.qualification_rate_pct) },
                    { header: "Avg Score", align: "right", render: r => fmtN(r.avg_our_score) },
                    { header: "H1 Rate", align: "right", render: r => r.our_h1_rate_pct == null ? "—" : fmtPctV(r.our_h1_rate_pct) },
                    { header: "Avg Field", align: "right", render: r => fmtN(r.avg_field_size) },
                    { header: "Mkt Non-Resp.", align: "right", render: r => fmtPctV(r.avg_market_non_responsive_pct) },
                ]}
            />
        </div>
    );
}

/* ══════════════════════════════════════════════════════════════
   MAIN COMPONENT
══════════════════════════════════════════════════════════════ */
function TechAnalyticsDeepDive({ TA }) {
    if (!TA) return null;

    const OV = TA.overview || {};
    const OP = TA.our_performance || {};
    const SS = TA.score_statistics || {};
    const CO = TA.competition || {};
    const CA = TA.competitor_analysis || {};
    const SEG = TA.segments || {};
    const PM = TA.probability_model || {};
    const FL = TA.financial_linkage || {};
    const MT = TA.monthly_trend || [];
    const INS = TA.insights || [];
    const DQ = TA.data_quality || {};

    const ourScoreHist = (OP.our_score_histogram || []).map((b, i) => ({ key: b.range, count: b.count, color: PAL[i % PAL.length] }));
    const allScoreHist = (SS.score_histogram || []).map((b, i) => ({ key: b.range, count: b.count, color: PAL[i % PAL.length] }));
    const monthlyTenderBars = MT.map(m => ({ key: m.month, count: m.tenders, color: C.p400 }));
    const monthlyQualBars = MT.map(m => ({ key: m.month, count: m.qualified, color: C.ok }));

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 2 }}>
                <span style={{ width: 3, height: 16, background: C.l500, borderRadius: 2 }} />
                <span style={{ fontSize: 12.5, fontWeight: 800, color: C.n900 }}>Technical Result Analytics — Deep Dive</span>
                <span style={{ fontSize: 9, color: C.n400 }}>bidder-level scoring, competitors & probability model</span>
            </div>

            {/* ── Overview ── */}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <Stat icon={<I.Doc />} label="Analyzed Tenders" value={OV.total_tenders ?? "—"} sub={`${fmtPctV(OV.bidder_data_coverage_pct)} bidder coverage`} color={C.p500} bg={C.p50} />
                <Stat icon={<I.Star />} label="Competitor Entries" value={OV.total_competitor_entries ?? "—"} sub={`${OV.unique_competitors ?? "—"} unique rivals`} color={C.r500} bg={C.r100} />
                <Stat icon={<I.Book />} label="Avg Field Size" value={fmtN(OV.field_size?.mean)} sub={`median ${fmtN(OV.field_size?.median)} · range ${OV.field_size?.min ?? "—"}–${OV.field_size?.max ?? "—"}`} color={C.p600} bg={C.p50} />
                <Stat icon={<I.Check />} label="Full Bidder-Data Coverage" value={OV.tenders_with_bidder_data ?? "—"} color={C.ok} bg={C.okBg} />
            </div>

            {/* ── Our Performance ── */}
            <Card title="Our Performance" subtitle="Qualification, tech-H1 and ranking once bids are decided">
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
                    <ProbStat icon={<I.Check />} label="Qualification Rate" prob={OP.qualification_probability} color={C.ok} bg={C.okBg} />
                    <ProbStat icon={<I.Trophy />} label="Tech H1 Rate" prob={OP.tech_h1_probability} color={C.p500} bg={C.p50} />
                    <ProbStat icon={<I.Star />} label="Top-3 Rate" prob={OP.top3_probability} color={C.r500} bg={C.r100} />
                    <Stat icon={<I.Book />} label="Avg Rank" value={fmtN(OP.avg_rank, 2)} color={C.n700} bg={C.n150} />
                    <Stat icon={<I.Info />} label="Avg Percentile in Field" value={fmtPctV(OP.avg_percentile_in_field)} color={C.p600} bg={C.p50} />
                    <Stat icon={<I.Alert />} label="Avg Gap to H1 (when not H1)" value={fmtN(OP.avg_gap_to_h1_when_not_h1)} color={C.warn} bg={C.warnBg} />
                    <Stat icon={<I.Trophy />} label="Avg Winning Margin (when H1)" value={fmtN(OP.avg_winning_margin_when_h1)} color={C.ok} bg={C.okBg} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                    <DistTable title="Our Tech Score" dist={OP.our_score} />
                    <div>
                        <div style={{ fontSize: 9.5, fontWeight: 700, color: C.n700, marginBottom: 6 }}>Our Score Distribution</div>
                        {ourScoreHist.length ? <HBar data={ourScoreHist} total={ourScoreHist.reduce((a, d) => a + d.count, 0)} compact /> : <Empty msg="No score data." />}
                    </div>
                </div>
                <div>
                    <div style={{ fontSize: 9.5, fontWeight: 700, color: C.n700, marginBottom: 6 }}>Closest Misses — near-wins worth revisiting</div>
                    <RankTable
                        rows={OP.closest_misses || []}
                        keyField="tender_id"
                        columns={[
                            { header: "Tender", wrap: true, render: r => <span title={r.tender_name}>{truncate(r.tender_name, 62)}</span> },
                            { header: "Our Score", align: "right", render: r => fmtN(r.our_score) },
                            { header: "Top Score", align: "right", render: r => fmtN(r.top_score) },
                            { header: "Gap", align: "right", render: r => <b style={{ color: C.warn }}>{fmtN(r.gap, 2)}</b> },
                        ]}
                    />
                </div>
            </Card>

            {/* ── Score Statistics ── */}
            <Card title="Score Statistics" subtitle="Our scores against the market, all bidders and top scorers">
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12, marginBottom: 12 }}>
                    <DistTable title="All Bidders" dist={SS.all_bidders_score} />
                    <DistTable title="Competitors Only" dist={SS.competitor_score} />
                    <DistTable title="Top Score / Tender" dist={SS.top_score_per_tender} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                    <div>
                        <div style={{ fontSize: 9.5, fontWeight: 700, color: C.n700, marginBottom: 6 }}>Score Distribution — All Entries</div>
                        {allScoreHist.length ? <HBar data={allScoreHist} total={allScoreHist.reduce((a, d) => a + d.count, 0)} compact /> : <Empty msg="No data." />}
                    </div>
                    <Card title="Our Avg vs Competitor Avg" style={{ boxShadow: "none", border: `1px solid ${C.n200}`, padding: "10px 12px" }}>
                        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, marginBottom: 8 }}>
                            <div style={{ textAlign: "center", background: C.okBg, border: `1px solid ${C.okBd}`, borderRadius: 7, padding: 8 }}>
                                <div style={{ fontSize: 16, fontWeight: 800, color: C.ok }}>{fmtN(SS.our_score_vs_competitors?.our_mean)}</div>
                                <div style={{ fontSize: 8.5, color: C.n500 }}>Our Mean</div>
                            </div>
                            <div style={{ textAlign: "center", background: C.n100, border: `1px solid ${C.n200}`, borderRadius: 7, padding: 8 }}>
                                <div style={{ fontSize: 16, fontWeight: 800, color: C.n700 }}>{fmtN(SS.our_score_vs_competitors?.competitor_mean)}</div>
                                <div style={{ fontSize: 8.5, color: C.n500 }}>Competitor Mean</div>
                            </div>
                        </div>
                        <div style={{ textAlign: "center", fontSize: 10.5, color: C.ok, fontWeight: 700 }}>
                            +{fmtN(SS.our_score_vs_competitors?.difference)} pts ahead on average
                        </div>
                    </Card>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <DistTable title="Score Spread per Tender" dist={SS.score_spread_per_tender} />
                    <DistTable title="H1 Margin per Tender" dist={SS.h1_margin_per_tender} />
                </div>
            </Card>

            {/* ── Competition Landscape ── */}
            <Card title="Competition Landscape" subtitle="Field size and non-responsiveness patterns across the market">
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
                    <ProbStat icon={<I.Alert />} label="Market Non-Responsive Rate" prob={CO.market_non_responsive_probability} color={C.err} bg={C.errBg} />
                    <ProbStat icon={<I.Check />} label="Our Non-Responsive Rate" prob={CO.our_non_responsive_probability} color={C.ok} bg={C.okBg} />
                    <Stat icon={<I.Book />} label="Avg Responsive Bidders / Tender" value={fmtN(CO.avg_responsive_bidders_per_tender)} color={C.p500} bg={C.p50} />
                    <Stat icon={<I.Info />} label="Field Size ↔ Non-Resp. Correlation"
                        value={CO.correlation_field_size_vs_non_responsive_rate ? `r=${fmtN(CO.correlation_field_size_vs_non_responsive_rate.r, 2)}` : "—"}
                        sub={CO.correlation_field_size_vs_non_responsive_rate?.strength}
                        color={C.n700} bg={C.n150} />
                </div>
                <div style={{ fontSize: 9.5, fontWeight: 700, color: C.n700, marginBottom: 6 }}>By Field Size</div>
                <RankTable
                    rows={CO.field_size_distribution || []}
                    keyField="field_size"
                    columns={[
                        { header: "Field Size", field: "field_size" },
                        { header: "Tenders", field: "tenders", align: "right" },
                        { header: "Our Qual. Rate", align: "right", render: r => r.our_qualification?.rate_pct == null ? "—" : fmtPctV(r.our_qualification.rate_pct) },
                        { header: "Our Tech-H1 Rate", align: "right", render: r => r.our_tech_h1?.rate_pct == null ? "—" : fmtPctV(r.our_tech_h1.rate_pct) },
                        { header: "Avg Non-Resp.", align: "right", render: r => fmtPctV(r.avg_non_responsive_pct) },
                    ]}
                />
            </Card>

            {/* ── Competitor Analysis ── */}
            <Card title="Competitor Analysis" subtitle="Who we come up against most, and how each matchup goes">
                <div style={{ marginBottom: 10 }}>
                    <ProbStat icon={<I.Star />} label="Chance of Beating a Random Competitor" prob={CA.pairwise_beat_probability_vs_random_competitor} color={C.p500} bg={C.p50} />
                </div>
                {[
                    ["Most Frequent Rivals", CA.most_frequent],
                    ["Strongest Scorers", CA.strongest_scorers],
                    ["Toughest for Us (head-to-head)", CA.toughest_for_us],
                    ["Frequently Non-Responsive", CA.frequently_non_responsive],
                ].map(([title, rows]) => (
                    <div key={title} style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 9.5, fontWeight: 700, color: C.n700, marginBottom: 6 }}>{title}</div>
                        <RankTable
                            rows={(rows || []).slice(0, 6)}
                            keyField="competitor"
                            columns={[
                                { header: "Competitor", field: "competitor", wrap: true },
                                { header: "Seen", field: "participations", align: "right" },
                                { header: "Avg Score", align: "right", render: r => fmtN(r.avg_score) },
                                { header: "Tech-H1 %", align: "right", render: r => r.tech_h1_rate_pct == null ? "—" : fmtPctV(r.tech_h1_rate_pct) },
                                { header: "Non-Resp. %", align: "right", render: r => fmtPctV(r.non_responsive_rate_pct) },
                                { header: "Our Win Rate (H2H)", align: "right", render: r => r.head_to_head_vs_us?.our_win_rate_pct == null ? "—" : `${fmtPctV(r.head_to_head_vs_us.our_win_rate_pct)} (n=${r.head_to_head_vs_us.tenders_compared})` },
                            ]}
                        />
                    </div>
                ))}
            </Card>

            {/* ── Segments ── */}
            <Card title="Performance by Segment" subtitle="Qualification, score & win rate broken out by dimension">
                <SegmentExplorer segments={SEG} />
            </Card>

            {/* ── Probability Model ── */}
            <Card title="Probability Model" subtitle="Statistical view of qualification & tech-H1 chances">
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
                    <ProbStat icon={<I.Check />} label="Qualification Probability" prob={PM.qualification_probability} color={C.ok} bg={C.okBg} />
                    <ProbStat icon={<I.Trophy />} label="Tech-H1 Probability" prob={PM.tech_h1_probability} color={C.p500} bg={C.p50} />
                    <Stat icon={<I.Star />} label="Lift vs Random Chance"
                        value={PM.chance_baseline ? `${fmtN(PM.chance_baseline.lift_vs_random, 2)}×` : "—"}
                        sub={PM.chance_baseline ? `us ${fmtPctV(PM.chance_baseline.actual_h1_rate_pct)} vs random ${fmtPctV(PM.chance_baseline.expected_h1_rate_if_random_pct)}` : undefined}
                        color={C.r500} bg={C.r100} />
                    <Stat icon={<I.Info />} label="Score ↔ Price-Ratio Correlation"
                        value={PM.correlation_tech_score_vs_price_ratio ? `r=${fmtN(PM.correlation_tech_score_vs_price_ratio.r, 2)}` : "—"}
                        sub={PM.correlation_tech_score_vs_price_ratio?.strength}
                        color={C.n700} bg={C.n150} />
                </div>
                {PM.score_target_model && (
                    <div>
                        <div style={{ fontSize: 9.5, fontWeight: 700, color: C.n700, marginBottom: 4 }}>Score Needed for a Target Tech-H1 Chance</div>
                        <div style={{ fontSize: 8.5, color: C.n400, marginBottom: 8 }}>
                            Model: competitor scores ~ Normal(μ={fmtN(PM.score_target_model.competitor_mu)}, σ={fmtN(PM.score_target_model.competitor_sigma)});
                            assumes {PM.score_target_model.typical_responsive_competitors_k} typical responsive rival(s)
                        </div>
                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                            {(PM.score_target_model.score_needed_for_target_h1_probability || []).map(t => (
                                <div key={t.target_h1_probability_pct} style={{ background: C.p50, border: `1px solid ${C.p100}`, borderRadius: 7, padding: "7px 12px", textAlign: "center" }}>
                                    <div style={{ fontSize: 15, fontWeight: 800, color: C.p600 }}>{fmtN(t.required_score)}</div>
                                    <div style={{ fontSize: 8, color: C.n500 }}>score for {t.target_h1_probability_pct}% H1 chance</div>
                                </div>
                            ))}
                            <div style={{ background: C.okBg, border: `1px solid ${C.okBd}`, borderRadius: 7, padding: "7px 12px", textAlign: "center" }}>
                                <div style={{ fontSize: 15, fontWeight: 800, color: C.ok }}>{fmtPctV(PM.score_target_model.prob_tech_h1_at_our_avg_score_pct)}</div>
                                <div style={{ fontSize: 8, color: C.n500 }}>at our current avg score</div>
                            </div>
                        </div>
                    </div>
                )}
            </Card>

            {/* ── Financial Linkage ── */}
            <Card title="Financial Linkage" subtitle="Does technical strength translate into winning the contract?">
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <Stat icon={<I.Doc />} label="Tenders w/ Price Data" value={FL.tenders_with_price_data ?? 0} color={C.p500} bg={C.p50} />
                    <Stat icon={<I.Trophy />} label="Tenders Where We Are L1" value={FL.tenders_where_we_are_l1 ?? 0} color={C.ok} bg={C.okBg} />
                    <ProbStat icon={<I.Star />} label="Tech-Top Is Also L1" prob={FL.tech_top_is_also_l1} color={C.p500} bg={C.p50} />
                    <ProbStat icon={<I.Trophy />} label="Tech-Top Wins Contract" prob={FL.tech_top_wins_contract} color={C.r500} bg={C.r100} />
                    <ProbStat icon={<I.Trophy />} label="L1 Conversion from Qualified" prob={FL.l1_conversion_from_qualified} color={C.ok} bg={C.okBg} />
                </div>
                {FL.price_spread_pct?.count > 0 && (
                    <div style={{ marginTop: 12 }}>
                        <DistTable title="Price Spread %" dist={FL.price_spread_pct} unit="%" />
                    </div>
                )}
            </Card>

            {/* ── Monthly Trend ── */}
            <Card title="Monthly Trend" subtitle="Volume, qualification and score over time">
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                    <div>
                        <div style={{ fontSize: 9.5, fontWeight: 700, color: C.n700, marginBottom: 6 }}>Tenders Analyzed</div>
                        {monthlyTenderBars.length ? <HBar data={monthlyTenderBars} total={monthlyTenderBars.reduce((a, d) => a + d.count, 0)} compact /> : <Empty msg="No data." />}
                    </div>
                    <div>
                        <div style={{ fontSize: 9.5, fontWeight: 700, color: C.n700, marginBottom: 6 }}>Qualified</div>
                        {monthlyQualBars.length ? <HBar data={monthlyQualBars} total={monthlyQualBars.reduce((a, d) => a + d.count, 0)} compact /> : <Empty msg="No data." />}
                    </div>
                </div>
                <RankTable
                    rows={MT}
                    keyField="month"
                    columns={[
                        { header: "Month", field: "month" },
                        { header: "Tenders", field: "tenders", align: "right" },
                        { header: "Qualified", field: "qualified", align: "right" },
                        { header: "Non-Resp.", field: "non_responsive", align: "right" },
                        { header: "Qual. Rate", align: "right", render: r => fmtPctV(r.qualification_rate_pct) },
                        { header: "Avg Score", align: "right", render: r => fmtN(r.avg_our_score) },
                        { header: "Avg Field", align: "right", render: r => fmtN(r.avg_field_size) },
                        { header: "H1 Count", field: "our_h1_count", align: "right" },
                    ]}
                />
            </Card>

            {/* ── Insights ── */}
            {INS.length > 0 && (
                <Card title="Key Insights" subtitle="Auto-generated from the numbers above">
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {INS.map((line, i) => (
                            <div key={i} style={{ display: "flex", gap: 7, alignItems: "flex-start", padding: "6px 9px", background: C.p50, border: `1px solid ${C.p100}`, borderRadius: 7 }}>
                                <span style={{ color: C.p500, flexShrink: 0, marginTop: 1 }}><I.Info /></span>
                                <span style={{ fontSize: 10, color: C.n700, lineHeight: 1.5 }}>{line}</span>
                            </div>
                        ))}
                    </div>
                </Card>
            )}

            {/* ── Data Quality ── */}
            <Card title="Data Quality" subtitle="How complete this analysis is — treat low-coverage sections with caution">
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <Stat icon={<I.Doc />} label="Tenders w/o Bidder Rows" value={DQ.tenders_without_bidder_rows ?? 0} color={DQ.tenders_without_bidder_rows ? C.warn : C.ok} bg={DQ.tenders_without_bidder_rows ? C.warnBg : C.okBg} />
                    <Stat icon={<I.Alert />} label="Tenders w/o Our Score" value={DQ.tenders_without_our_score ?? 0} color={DQ.tenders_without_our_score ? C.warn : C.ok} bg={DQ.tenders_without_our_score ? C.warnBg : C.okBg} />
                    <Stat icon={<I.Info />} label="Competitor Rows w/o Score/Status" value={DQ.competitor_entries_without_score_or_status ?? 0} color={C.n700} bg={C.n150} />
                    <Stat icon={<I.Check />} label="Our Rows Matched in Bidder Table" value={DQ.our_rows_matched_in_bidder_table ?? 0} color={C.p500} bg={C.p50} />
                </div>
                {DQ.our_name_keywords_configured === false && (
                    <div style={{ marginTop: 9, fontSize: 9.5, color: C.warn, display: "flex", alignItems: "center", gap: 5 }}>
                        <I.Alert /> Our-company name keywords aren't configured on the backend — some "our score" matches may be getting missed.
                    </div>
                )}
            </Card>
        </div>
    );
}
/* ══════════════════════════════════════════════════════
   TAB: TECHNICAL RESULT ANALYTICS  (NEW)
   ───────────────────────────────────────────────────────
   Sits on top of the same Tech Open (5→11) data as TechTab,
   but is built purely for reading the *result*: how scores
   are distributed, how score correlates with becoming L1
   once a tender reaches Financial Open, which sectors/TLs
   are converting best, and a fast leaderboard of top scores
   and confirmed wins. Uses the existing chart primitives —
   no new dependency, no new endpoint.
══════════════════════════════════════════════════════ */
function TechResultAnalyticsTab({ gp, onFunnel }) {
    const { f, set, remove, clear } = useFilters(["sector", "type", "client", "state", "tl"]);
    const params = { ...gp, tab: "11", sector: f.sector, tender_type: f.type, client: f.client, state: f.state, [TL_QUERY_PARAM]: f.tl };
    const { data, loading, error } = useDashboard(params);
    useEffect(() => { if (data?.funnel && onFunnel) onFunnel(data.funnel); }, [data, onFunnel]);

    const rawT = data?.tenders || [];
    const T = useMemo(() => f.tl ? rawT.filter(t => (t.tl_details?.assigned_tls || []).some(a => a.name === f.tl)) : rawT, [rawT, f.tl]);

    const A = data?.analytics || {};
    const mainTrend = data?.trend_overview || {};
    const hasFilters = !!(f.sector || f.type || f.client || f.state || f.tl);
    const { trend: unfilteredTrend } = useUnfilteredTrend(params.tab, gp.filter, !hasFilters);
    const trend = {
        ...mainTrend,
        sector_trend: unfilteredTrend?.sector_trend || mainTrend.sector_trend,
        type_trend: unfilteredTrend?.type_trend || mainTrend.type_trend,
        monthly_breakdown: mainTrend.monthly_breakdown || unfilteredTrend?.monthly_breakdown,
    };

    const f511 = data?.funnel?.tab5_to_tab11 || {};
    const cr = f511.conversion_rate_pct ?? 0;

    const scored = T.filter(t => t.tech_score !== null && t.tech_score !== undefined && t.tech_score !== "");
    const responsive = scored.filter(t => parseFloat(t.tech_score) >= 60);
    const nonResponsive = scored.length - responsive.length;
    const avgScore = f511.tech_score_stats?.avg_tech_score
        ?? (scored.length ? Math.round(scored.reduce((a, t) => a + (parseFloat(t.tech_score) || 0), 0) / scored.length) : 0);

    // Tenders that have already been decided L1 / non-L1 at Financial Open
    const decided = T.filter(t => t.are_we_l1 === true || t.are_we_l1 === false);
    const l1Won = decided.filter(t => t.are_we_l1);
    const winRate = pct(l1Won.length, decided.length);

    const emdData = useMemo(() => Object.entries(A.emd_analytics || {}).map(([k, c], i) => ({ key: k, count: c, color: PAL[i] })).filter(d => d.count > 0), [A]);
    const sectorData = useMemo(() => Object.entries(A.sector_wise_counts || {}).map(([k, c], i) => ({ key: k, count: c, color: SEC_COL[k] || PAL[i] })).sort((a, b) => b.count - a.count), [A]);
    const tlSrc = A?.tl_summary?.assigned_tls || [];

    // Score-bucket distribution + win-rate correlation per bucket
    const BUCKETS = [
        { key: "0–39 (NR)", min: 0, max: 39, color: C.err },
        { key: "40–59", min: 40, max: 59, color: C.warn },
        { key: "60–79 (R)", min: 60, max: 79, color: C.ok },
        { key: "80–100", min: 80, max: 100, color: C.p400 },
        { key: ">100", min: 101, max: Infinity, color: C.l500 },
    ];
    const scoreD = BUCKETS
        .map(b => ({ key: b.key, count: scored.filter(t => { const s = parseFloat(t.tech_score); return s >= b.min && s <= b.max; }).length, color: b.color }))
        .filter(d => d.count > 0);

    const scoreWinRows = BUCKETS.map(b => {
        const bucketT = decided.filter(t => { const s = parseFloat(t.tech_score); return s >= b.min && s <= b.max; });
        const won = bucketT.filter(t => t.are_we_l1).length;
        return { ...b, total: bucketT.length, won, rate: pct(won, bucketT.length) };
    }).filter(r => r.total > 0);

    // Sector-wise avg score + win rate
    const sectorRows = sectorData.map(s => {
        const st = T.filter(t => t.sector === s.key && t.tech_score !== null && t.tech_score !== undefined && t.tech_score !== "");
        const avg = st.length ? Math.round(st.reduce((a, t) => a + (parseFloat(t.tech_score) || 0), 0) / st.length) : 0;
        const dec = T.filter(t => t.sector === s.key && (t.are_we_l1 === true || t.are_we_l1 === false));
        const won = dec.filter(t => t.are_we_l1).length;
        return { ...s, avg, decTotal: dec.length, won, rate: pct(won, dec.length) };
    });

    // Leaderboards
    const topScored = [...scored].sort((a, b) => parseFloat(b.tech_score) - parseFloat(a.tech_score)).slice(0, 5);
    const topWins = l1Won.slice(0, 5);

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {loading && <Loader />}{error && <ErrBanner />}
            <InfoBanner title="Technical Result Analytics (5→11)" items={[
                { label: "Submitted", value: f511.total_submitted ?? 0, color: C.p500 },
                { label: "Tech Open", value: f511.moved_to_tab11 ?? T.length, color: C.ok },
                { label: "Conversion", value: `${cr}%`, color: cr >= 5 ? C.ok : C.warn },
                { label: "Avg Score", value: avgScore || "—", color: C.r500 },
                { label: "Decided L1/Non-L1", value: decided.length, color: C.p600 },
                { label: "Win Rate", value: decided.length ? `${winRate}%` : "—", color: winRate > 50 ? C.ok : C.warn },
            ]} />
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <Stat icon={<I.Book />} label="Tech Open" value={f511.moved_to_tab11 ?? T.length} color={C.p500} bg={C.p50} />
                <Stat icon={<I.Check />} label="Responsive (≥60)" value={responsive.length} color={C.ok} bg={C.okBg} />
                <Stat icon={<I.Alert />} label="Non-Responsive" value={nonResponsive} color={C.err} bg={C.errBg} />
                <Stat icon={<I.Star />} label="Avg Tech Score" value={avgScore || "—"} color={avgScore >= 60 ? C.ok : C.warn} bg={avgScore >= 60 ? C.okBg : C.warnBg} />
                <Stat icon={<I.Trophy />} label="We are L1" value={l1Won.length} sub={decided.length ? `${winRate}% of decided` : undefined} subUp={winRate > 50} color={C.ok} bg={C.okBg} />
                <Stat icon={<I.Star />} label="Conversion" value={`${cr}%`} color={cr >= 5 ? C.ok : C.warn} bg={cr >= 5 ? C.okBg : C.warnBg} />
            </div>
            <ActiveChips filters={{ sector: f.sector, type: f.type, client: f.client, state: f.state, tl: f.tl ? tlDisplayLabel(f.tl, A) : null }} onRemove={remove} onClearAll={clear} />
            <STTRow analytics={A} trend={trend} activeSector={f.sector} activeType={f.type} onSector={v => set("sector", v)} onType={v => set("type", v)} />

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Card title="Score Distribution" subtitle="Tech score buckets across tenders">
                    {scoreD.length > 0 ? <HBar data={scoreD} total={scored.length} /> : <Empty msg="No tech scores yet." />}
                </Card>
                <Card title="Score vs Win Rate" subtitle="How win rate moves with score bucket (decided tenders only)">
                    {scoreWinRows.length > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            {scoreWinRows.map(r => (
                                <div key={r.key}>
                                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                                        <span style={{ fontSize: 10, color: C.n700 }}>{r.key}</span>
                                        <div style={{ display: "flex", gap: 7 }}>
                                            <span style={{ fontSize: 9, color: C.n500 }}>{r.won}/{r.total} won</span>
                                            <span style={{ fontSize: 10.5, fontWeight: 700, color: r.rate >= 50 ? C.ok : C.warn }}>{r.rate}%</span>
                                        </div>
                                    </div>
                                    <ProgBar value={r.won} max={r.total || 1} color={r.color} />
                                </div>
                            ))}
                        </div>
                    ) : <Empty msg="No decided (L1 / non-L1) tenders yet in this score range." />}
                </Card>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Card title="Sector Performance" subtitle="Avg score & win rate by sector">
                    {sectorRows.length > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                            {sectorRows.map(s => (
                                <div key={s.key} style={{ padding: "6px 8px", border: `1px solid ${C.n200}`, borderRadius: 7, background: C.n100 }}>
                                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                            <span style={{ width: 6, height: 6, borderRadius: 1, background: s.color }} />
                                            <span style={{ fontSize: 10.5, fontWeight: 600, color: C.n700 }}>{s.key}</span>
                                        </div>
                                        <span style={{ fontSize: 9, color: C.n500 }}>{s.count} tenders</span>
                                    </div>
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                                        <div>
                                            <div style={{ fontSize: 8.5, color: C.n500, marginBottom: 1 }}>Avg Score</div>
                                            <ProgBar value={s.avg} max={100} color={s.avg >= 60 ? C.ok : s.avg >= 40 ? C.warn : C.err} />
                                        </div>
                                        <div>
                                            <div style={{ fontSize: 8.5, color: C.n500, marginBottom: 1 }}>Win Rate ({s.won}/{s.decTotal})</div>
                                            <ProgBar value={s.won} max={s.decTotal || 1} color={s.rate >= 50 ? C.ok : C.warn} />
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : <Empty msg="No data." />}
                </Card>
                <TLCard tlSource={tlSrc} analytics={A} title="TL-wise Distribution" subtitle="Tenders handled per TL at this stage" onFilter={v => set("tl", v)} active={f.tl} />
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                <Card title="EMD Distribution"><Donut data={emdData} size={120} /></Card>
                <Card title="Top Tech Scores" subtitle="Highest-scoring tenders">
                    {topScored.length > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                            {topScored.map((t, i) => (
                                <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 7, padding: "5px 7px", borderRadius: 6, background: i === 0 ? C.okBg : C.n100, border: `1px solid ${i === 0 ? C.okBd : C.n200}` }}>
                                    <span style={{ fontSize: 9.5, fontWeight: 800, color: i === 0 ? C.ok : C.n500, minWidth: 14 }}>{i + 1}</span>
                                    <span style={{ fontSize: 9.5, color: C.n800, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={t.tender_name}>{t.tender_name}</span>
                                    <span style={{ fontSize: 10.5, fontWeight: 700, color: i === 0 ? C.ok : C.p600 }}>{t.tech_score}</span>
                                </div>
                            ))}
                        </div>
                    ) : <Empty msg="No scored tenders." />}
                </Card>
                <Card title="Recent L1 Wins" subtitle="Tenders confirmed as L1">
                    {topWins.length > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                            {topWins.map(t => (
                                <div key={t.id} style={{ display: "flex", alignItems: "center", gap: 7, padding: "5px 7px", borderRadius: 6, background: C.okBg, border: `1px solid ${C.okBd}` }}>
                                    <span style={{ color: C.ok }}><I.Trophy /></span>
                                    <span style={{ fontSize: 9.5, color: C.n800, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={t.tender_name}>{t.tender_name}</span>
                                    <span style={{ fontSize: 9.5, fontWeight: 700, color: C.ok }}>{t.tech_score ?? "—"}</span>
                                </div>
                            ))}
                        </div>
                    ) : <Empty msg="No confirmed L1 wins yet." />}
                </Card>
            </div>

            <ClientStateRow analytics={A} activeClient={f.client} activeState={f.state} onClient={v => set("client", v)} onState={v => set("state", v)} />
            <TechAnalyticsDeepDive TA={data?.technical_result_analytics} />

            {T.length > 0 ? (
                <TTable
                    tenders={T}
                    extraCols={[
                        { header: "Score", render: t => t.tech_score ?? "—" },
                        { header: "Responsive", render: t => t.responsive_status || (parseFloat(t.tech_score) >= 60 ? "Responsive" : t.tech_score ? "Non Responsive" : "—") },
                        { header: "L1?", render: t => t.are_we_l1 === true ? "✓" : t.are_we_l1 === false ? "No" : "—" },
                    ]}
                    onClient={v => set("client", v)} activeClient={f.client}
                    onState={v => set("state", v)} activeState={f.state}
                    exportParams={params} exportFilename="technical_result_analytics.xlsx"
                />
            ) : !loading && <Empty />}
        </div>
    );
}


/* ══════════════════════════════════════════════════════════════
   FINANCIAL RESULT ANALYTICS — paste into TenderDashboard.jsx
   (anywhere top-level, below TechAnalyticsDeepDive). Reuses Card,
   Stat, ProbStat, DistTable, RankTable, HBar, Empty, fmtN, fmtPctV,
   truncate, C, PAL, I, useFilters, useDashboard, etc.
══════════════════════════════════════════════════════════════ */

const FIN_SEG_LABELS = { sector_wise: "Sector", tender_type_wise: "Tender Type", client_wise: "Client", state_wise: "State" };

function FinSegmentExplorer({ segments }) {
    const keys = Object.keys(segments || {}).filter(k => (segments[k] || []).length);
    const [sel, setSel] = useState(keys[0] || null);
    useEffect(() => { if ((!sel || !keys.includes(sel)) && keys.length) setSel(keys[0]); }, [keys, sel]);
    if (!keys.length) return <Empty msg="No segment data." />;
    return (
        <div>
            <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginBottom: 9 }}>
                {keys.map(k => (
                    <button key={k} onClick={() => setSel(k)}
                        style={{ padding: "4px 10px", border: `1px solid ${sel === k ? C.p400 : C.n200}`, borderRadius: 6, background: sel === k ? C.p500 : "transparent", color: sel === k ? C.white : C.n600, fontSize: 9.5, fontWeight: sel === k ? 700 : 400, cursor: "pointer" }}>
                        {FIN_SEG_LABELS[k] || k}
                    </button>
                ))}
            </div>
            <RankTable
                rows={segments[sel] || []}
                keyField="segment"
                columns={[
                    { header: FIN_SEG_LABELS[sel] || "Segment", field: "segment", wrap: true },
                    { header: "Tenders", field: "tenders", align: "right" },
                    { header: "Our Price", field: "with_our_price", align: "right" },
                    { header: "Price-L1", field: "price_l1_count", align: "right" },
                    { header: "L1 Rate", align: "right", render: r => r.price_l1_rate_pct == null ? "—" : fmtPctV(r.price_l1_rate_pct) },
                    { header: "Avg Gap to L1", align: "right", render: r => r.avg_gap_to_l1_pct == null ? "—" : fmtPctV(r.avg_gap_to_l1_pct) },
                    { header: "Avg Margin (L1)", align: "right", render: r => r.avg_margin_when_l1_pct == null ? "—" : fmtPctV(r.avg_margin_when_l1_pct) },
                    { header: "Avg Bids", align: "right", render: r => fmtN(r.avg_bids) },
                ]}
            />
        </div>
    );
}

function FinAnalyticsDeepDive({ FA }) {
    if (!FA) return null;
    if (!FA.overview) return <Empty msg={FA.message || "No financial analytics available."} />;

    const OV = FA.overview, OP = FA.our_pricing || {}, EC = FA.estimate_comparison || {};
    const TP = FA.tech_price_interplay || {}, CP = FA.competitor_pricing || {};
    const MT = FA.monthly_trend || [], INS = FA.insights || [], DQ = FA.data_quality || {};
    const toBars = (arr, cols) => (arr || []).map((b, i) => ({ key: b.range, count: b.count, color: cols ? cols[i % cols.length] : PAL[i % PAL.length] }));
    const rankBars = toBars(OP.rank_distribution, [C.ok, C.p400, C.r500, C.warn]);
    const gapBars = toBars(OP.gap_distribution, [C.ok, C.p400, C.warn, C.err, "#7f1d1d"]);
    const corr = TP.correlation_tech_rank_vs_price_rank;

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 2 }}>
                <span style={{ width: 3, height: 16, background: C.ok, borderRadius: 2 }} />
                <span style={{ fontSize: 12.5, fontWeight: 800, color: C.n900 }}>Financial Result Analytics</span>
                <span style={{ fontSize: 9, color: C.n400 }}>price ranking, gap to L1, competitor pricing (L1 = lowest quoted price)</span>
            </div>

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <Stat icon={<I.Doc />} label="Tenders w/ Price Data" value={OV.tenders_with_price_data ?? "—"} sub={`${fmtPctV(OV.price_coverage_pct)} of ${OV.total_tenders}`} color={C.p500} bg={C.p50} />
                <Stat icon={<I.Money />} label="Our Price Available" value={OV.tenders_with_our_price ?? "—"} color={C.p600} bg={C.p50} />
                <Stat icon={<I.Book />} label="Avg Bids / Tender" value={fmtN(OV.bids_per_tender?.mean)} sub={`median ${fmtN(OV.bids_per_tender?.median)}`} color={C.r500} bg={C.r100} />
                <Stat icon={<I.Info />} label="Avg Price Spread" value={fmtPctV(OV.price_spread_pct?.mean)} sub={`median ${fmtPctV(OV.price_spread_pct?.median)}`} color={C.n700} bg={C.n150} />
            </div>

            <Card title="Our Pricing Performance" subtitle="Where we land on price when bids are opened">
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 12 }}>
                    <ProbStat icon={<I.Trophy />} label="Price-L1 Rate" prob={OP.price_l1_probability} color={C.ok} bg={C.okBg} />
                    <ProbStat icon={<I.Star />} label="Price Top-3 Rate" prob={OP.price_top3_probability} color={C.p500} bg={C.p50} />
                    <ProbStat icon={<I.Check />} label="Contract Win Rate" prob={OP.contract_win_probability} color={C.ok} bg={C.okBg} />
                    <Stat icon={<I.Book />} label="Avg Price Rank" value={fmtN(OP.avg_price_rank, 2)} color={C.n700} bg={C.n150} />
                    <Stat icon={<I.Alert />} label="Avg Cut Needed (lost ≤10%)" value={fmtPctV(OP.avg_reduction_needed_pct_within_10)} color={C.warn} bg={C.warnBg} />
                    <Stat icon={<I.Money />} label="Avg Margin under L2 (when L1)" value={fmtPctV(OP.margin_when_l1_pct?.mean)} color={C.ok} bg={C.okBg} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                    <div>
                        <div style={{ fontSize: 9.5, fontWeight: 700, color: C.n700, marginBottom: 6 }}>Our Price Rank</div>
                        {rankBars.some(b => b.count) ? <HBar data={rankBars} total={rankBars.reduce((a, d) => a + d.count, 0)} compact /> : <Empty msg="No data." />}
                    </div>
                    <div>
                        <div style={{ fontSize: 9.5, fontWeight: 700, color: C.n700, marginBottom: 6 }}>Gap to L1 when we lost</div>
                        {gapBars.some(b => b.count) ? <HBar data={gapBars} total={gapBars.reduce((a, d) => a + d.count, 0)} compact /> : <Empty msg="No lost tenders." />}
                    </div>
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
                    <DistTable title="Gap to L1 % (lost)" dist={OP.gap_to_l1_pct} unit="%" />
                    <DistTable title="Margin under L2 % (won)" dist={OP.margin_when_l1_pct} unit="%" />
                </div>
                <div style={{ fontSize: 9.5, fontWeight: 700, color: C.n700, marginBottom: 6 }}>Closest Price Misses — small cuts would have won</div>
                <RankTable
                    rows={OP.closest_price_misses || []}
                    keyField="tender_id"
                    columns={[
                        { header: "Tender", wrap: true, render: r => <span title={r.tender_name}>{truncate(r.tender_name, 56)}</span> },
                        { header: "Our Bid", align: "right", render: r => fmtL(r.our_amount) },
                        { header: "L1 Bid", align: "right", render: r => fmtL(r.l1_amount) },
                        { header: "L1 Bidder", render: r => truncate(r.l1_bidder, 24) },
                        { header: "Gap", align: "right", render: r => <b style={{ color: C.warn }}>{fmtPctV(r.gap_pct, 2)}</b> },
                    ]}
                />
            </Card>

            <Card title="Bid vs Estimated Cost" subtitle={`${EC.tenders_with_estimate ?? 0} tenders with an estimate · % of estimate`}>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                    <DistTable title="Our Bid" dist={EC.our_pct_of_estimate} unit="%" />
                    <DistTable title="L1 Bid" dist={EC.l1_pct_of_estimate} unit="%" />
                    <DistTable title="All Bids" dist={EC.all_bids_pct_of_estimate} unit="%" />
                </div>
            </Card>

            <Card title="Technical vs Price" subtitle="Does technical strength turn into the lowest price and the award?">
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <ProbStat icon={<I.Star />} label="Price-L1 | Tech-H1" prob={TP.price_l1_given_tech_top} color={C.p500} bg={C.p50} />
                    <ProbStat icon={<I.Alert />} label="Price-L1 | Not Tech-H1" prob={TP.price_l1_given_not_tech_top} color={C.warn} bg={C.warnBg} />
                    <ProbStat icon={<I.Trophy />} label="Our Wins w/o Lowest Price" prob={TP.wins_without_lowest_price} color={C.ok} bg={C.okBg} />
                    <ProbStat icon={<I.Info />} label="Winner Not Lowest Bidder" prob={TP.winner_not_lowest_price} color={C.n700} bg={C.n150} />
                    <Stat icon={<I.Info />} label="Tech Rank ↔ Price Rank" value={corr ? `r=${fmtN(corr.r, 2)}` : "—"} sub={corr?.strength} color={C.n700} bg={C.n150} />
                </div>
            </Card>

            <Card title="Competitor Pricing" subtitle="Who undercuts the market and who wins the award">
                {[["Most Often Lowest Price", CP.most_price_l1], ["Most Aggressive (lowest avg premium over L1, ≥3 bids)", CP.most_aggressive], ["Most Financial Wins", CP.most_financial_wins]].map(([title, rows]) => (
                    <div key={title} style={{ marginBottom: 12 }}>
                        <div style={{ fontSize: 9.5, fontWeight: 700, color: C.n700, marginBottom: 6 }}>{title}</div>
                        <RankTable
                            rows={(rows || []).slice(0, 6)}
                            keyField="competitor"
                            columns={[
                                { header: "Competitor", field: "competitor", wrap: true },
                                { header: "Bids Seen", field: "participations", align: "right" },
                                { header: "Price-L1", field: "price_l1_count", align: "right" },
                                { header: "L1 Rate", align: "right", render: r => r.price_l1_rate_pct == null ? "—" : fmtPctV(r.price_l1_rate_pct) },
                                { header: "Avg Premium over L1", align: "right", render: r => r.avg_premium_over_l1_pct == null ? "—" : fmtPctV(r.avg_premium_over_l1_pct) },
                                { header: "Wins", field: "financial_wins", align: "right" },
                            ]}
                        />
                    </div>
                ))}
            </Card>

            <Card title="Pricing by Segment" subtitle="Price-L1 rate and gap broken out by dimension">
                <FinSegmentExplorer segments={FA.segments} />
            </Card>

            <Card title="Monthly Trend">
                <RankTable
                    rows={MT}
                    keyField="month"
                    columns={[
                        { header: "Month", field: "month" },
                        { header: "Tenders", field: "tenders", align: "right" },
                        { header: "Our Price", field: "with_our_price", align: "right" },
                        { header: "Price-L1", field: "price_l1", align: "right" },
                        { header: "L1 Rate", align: "right", render: r => r.price_l1_rate_pct == null ? "—" : fmtPctV(r.price_l1_rate_pct) },
                        { header: "Avg Gap to L1", align: "right", render: r => r.avg_gap_to_l1_pct == null ? "—" : fmtPctV(r.avg_gap_to_l1_pct) },
                        { header: "Avg Bids", align: "right", render: r => fmtN(r.avg_bids) },
                    ]}
                />
            </Card>

            {INS.length > 0 && (
                <Card title="Key Insights" subtitle="Auto-generated from the numbers above">
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {INS.map((line, i) => (
                            <div key={i} style={{ display: "flex", gap: 7, alignItems: "flex-start", padding: "6px 9px", background: C.p50, border: `1px solid ${C.p100}`, borderRadius: 7 }}>
                                <span style={{ color: C.p500, flexShrink: 0, marginTop: 1 }}><I.Info /></span>
                                <span style={{ fontSize: 10, color: C.n700, lineHeight: 1.5 }}>{line}</span>
                            </div>
                        ))}
                    </div>
                </Card>
            )}

            <Card title="Data Quality" subtitle="Treat low-coverage sections with caution">
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                    <Stat icon={<I.Doc />} label="No Price Comparison" value={DQ.tenders_without_price_comparison ?? 0} color={DQ.tenders_without_price_comparison ? C.warn : C.ok} bg={DQ.tenders_without_price_comparison ? C.warnBg : C.okBg} />
                    <Stat icon={<I.Alert />} label="Missing Our Price" value={DQ.tenders_without_our_price ?? 0} color={DQ.tenders_without_our_price ? C.warn : C.ok} bg={DQ.tenders_without_our_price ? C.warnBg : C.okBg} />
                    <Stat icon={<I.Info />} label="Missing Estimate" value={DQ.tenders_without_estimate ?? 0} color={C.n700} bg={C.n150} />
                    <Stat icon={<I.Check />} label="Our Rows Matched" value={DQ.our_rows_matched_in_bidder_table ?? 0} color={C.p500} bg={C.p50} />
                </div>
                {DQ.our_name_keywords_configured === false && (
                    <div style={{ marginTop: 9, fontSize: 9.5, color: C.warn, display: "flex", alignItems: "center", gap: 5 }}>
                        <I.Alert /> OUR_NAME_KEYWORDS is empty on the backend — our own bid rows may be missed and counted as a competitor.
                    </div>
                )}
            </Card>
        </div>
    );
}

/* ══════════════ TAB ══════════════ */
function FinancialResultAnalyticsTab({ gp, onFunnel }) {
    const { f, set, remove, clear } = useFilters(["sector", "type", "client", "state"]);
    const params = { ...gp, tab: "12", sector: f.sector, tender_type: f.type, client: f.client, state: f.state };
    const { data, loading, error } = useDashboard(params);
    useEffect(() => { if (data?.funnel && onFunnel) onFunnel(data.funnel); }, [data, onFunnel]);

    const T = data?.tenders || [];
    const A = data?.analytics || {};
    const f1112 = data?.funnel?.tab11_to_tab12 || {};
    const cr = f1112.conversion_rate_pct ?? 0;
    const mainTrend = data?.trend_overview || {};
    const hasFilters = !!(f.sector || f.type || f.client || f.state);
    const { trend: unfilteredTrend } = useUnfilteredTrend(params.tab, gp.filter, !hasFilters);
    const trend = {
        ...mainTrend,
        sector_trend: unfilteredTrend?.sector_trend || mainTrend.sector_trend,
        type_trend: unfilteredTrend?.type_trend || mainTrend.type_trend,
        monthly_breakdown: mainTrend.monthly_breakdown || unfilteredTrend?.monthly_breakdown,
    };

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {loading && <Loader />}{error && <ErrBanner />}
            <InfoBanner title="Financial Result Analytics (11→12)" items={[
                { label: "Tech Open", value: f1112.total_in_tab11 ?? 0, color: C.p500 },
                { label: "Fin. Open", value: f1112.moved_to_tab12 ?? T.length, color: C.ok },
                { label: "L1 Winners", value: f1112.winner_tender_count ?? 0, color: C.ok },
                { label: "Non-Winners", value: f1112.non_winner_count ?? 0, color: C.err },
                { label: "Conversion", value: `${cr}%`, color: cr >= 20 ? C.ok : C.warn },
            ]} />
            <ActiveChips filters={{ sector: f.sector, type: f.type, client: f.client, state: f.state }} onRemove={remove} onClearAll={clear} />
            <STTRow analytics={A} trend={trend} activeSector={f.sector} activeType={f.type} onSector={v => set("sector", v)} onType={v => set("type", v)} />
            <ClientStateRow analytics={A} activeClient={f.client} activeState={f.state} onClient={v => set("client", v)} onState={v => set("state", v)} />
            <FinAnalyticsDeepDive FA={data?.financial_result_analytics} />
            {T.length > 0 ? (
                <TTable tenders={T}
                    extraCols={[{ header: "Score", render: t => t.tech_score || "—" }, { header: "L1?", render: t => t.are_we_l1 ? "✓" : "No" }]}
                    onClient={v => set("client", v)} activeClient={f.client}
                    onState={v => set("state", v)} activeState={f.state}
                    exportParams={params} exportFilename="financial_result_analytics.xlsx" />
            ) : !loading && <Empty />}
        </div>
    );
}

/* ══════════════ WIRING (3 small edits) ══════════════

1) TABS array — add after the technical_result_analytics entry:
   { key: "financial_result_analytics", label: "Financial Result Analytics",
     info: "Price ranking, gap to L1, margin over L2, bid vs estimate, competitor pricing and tech-vs-price linkage for tenders in financial evaluation." },

2) App → tabCount — add:
   financial_result_analytics: f1112.moved_to_tab12,

3) App → render block — add:
   {tab === "financial_result_analytics" && <FinancialResultAnalyticsTab gp={gp} onFunnel={setFunnel} />}
*/
/* ══════════════════════════════════════════════════════
   TL NAME RESOLVER
   Builds Map<emp_code → name> from analytics.tl_summary
══════════════════════════════════════════════════════ */
function buildTLMap(analytics) {
    const map = {};
    (analytics?.tl_summary?.assigned_tls || []).forEach(t => {
        if (t.emp_code) map[t.emp_code] = t.name || t.emp_code;
    });
    return map;
}
function tlDisplayLabel(empCode, analytics) {
    if (!empCode) return empCode;
    return buildTLMap(analytics)[empCode] || empCode;
}

/* ══════════════════════════════════════════════════════
   ICONS
══════════════════════════════════════════════════════ */
const I = {
    Doc: () => <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" /><polyline points="14 2 14 8 20 8" /></svg>,
    Check: () => <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="9 11 12 14 22 4" /><path d="M21 12v7a2 2 0 01-2 2H5a2 2 0 01-2-2V5a2 2 0 012-2h11" /></svg>,
    Clock: () => <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><polyline points="12 6 12 12 16 14" /></svg>,
    Money: () => <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="12" y1="1" x2="12" y2="23" /><path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" /></svg>,
    Star: () => <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" /></svg>,
    Send: () => <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><line x1="22" y1="2" x2="11" y2="13" /><polygon points="22 2 15 22 11 13 2 9 22 2" /></svg>,
    Book: () => <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M2 3h6a4 4 0 014 4v14a3 3 0 00-3-3H2z" /><path d="M22 3h-6a4 4 0 00-4 4v14a3 3 0 013-3h7z" /></svg>,
    Trophy: () => <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polyline points="8 21 12 17 16 21" /><line x1="12" y1="17" x2="12" y2="11" /><path d="M7 4h10l1 7a5 5 0 01-5 5h-2a5 5 0 01-5-5l1-7z" /></svg>,
    Alert: () => <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" /><line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" /></svg>,
    Eye: () => <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></svg>,
    X: () => <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>,
    Search: () => <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></svg>,
    Info: () => <svg width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="10" /><line x1="12" y1="16" x2="12" y2="12" /><line x1="12" y1="8" x2="12.01" y2="8" /></svg>,
    Up: () => <svg width="10" height="10" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24"><polyline points="23 6 13.5 15.5 8.5 10.5 1 18" /><polyline points="17 6 23 6 23 12" /></svg>,
    Filter: () => <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" /></svg>,
    Export: () => <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>,
    Coin: () => <svg width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><circle cx="12" cy="12" r="9" /><path d="M9.5 15.2c.5.9 1.4 1.4 2.5 1.4 1.7 0 3-1 3-2.3 0-1.4-1.3-1.9-3-2.3-1.7-.4-3-.9-3-2.3 0-1.3 1.3-2.3 3-2.3 1.1 0 2 .5 2.5 1.4" /><line x1="12" y1="6.4" x2="12" y2="7.6" /><line x1="12" y1="16.6" x2="12" y2="17.6" /></svg>,
};

/* ══════════════════════════════════════════════════════
   HELPERS
══════════════════════════════════════════════════════ */
function fmtL(v) { if (!v) return "—"; if (v >= 10000000) return `₹${(v / 10000000).toFixed(1)}Cr`; if (v >= 100000) return `₹${(v / 100000).toFixed(1)}L`; return `₹${Math.round(v / 1000)}K`; }
function fmtCr(v) { if (!v) return "—"; const c = v / 10000000; return c >= 1 ? `₹${c.toFixed(1)}Cr` : `₹${(v / 100000).toFixed(1)}L`; }
function pct(a, b) { return b > 0 ? Math.round(a / b * 100) : 0; }
function dC(d) { return d <= 3 ? C.err : d <= 7 ? C.warn : C.ok; }
function dBg(d) { return d <= 3 ? C.errBg : d <= 7 ? C.warnBg : C.okBg; }
function dBd(d) { return d <= 3 ? C.errBd : d <= 7 ? C.warnBd : C.okBd; }
function tlName(t) { return t?.tl_details?.assigned_tls_names || t?.assigned_tls_summary?.names || "—"; }

/* ══════════════════════════════════════════════════════
   CHART: DONUT
══════════════════════════════════════════════════════ */
function Donut({ data, size = 140, onFilter, active }) {
    const [hov, setHov] = useState(null);
    if (!data || !data.length) return <div style={{ height: size, display: "flex", alignItems: "center", justifyContent: "center", color: C.n400, fontSize: 11 }}>No data</div>;
    const total = data.reduce((a, d) => a + d.count, 0) || 1;
    const cx = size / 2, cy = size / 2, r = size * 0.36, ir = r * 0.58;
    let cur = 0;
    const segs = data.slice(0, 12).map((d, i) => {
        const p = d.count / total;
        const s0 = cur * 2 * Math.PI - Math.PI / 2; cur += p;
        const s1 = cur * 2 * Math.PI - Math.PI / 2;
        const la = p > 0.5 ? 1 : 0;
        const [x1, y1] = [cx + r * Math.cos(s0), cy + r * Math.sin(s0)];
        const [x2, y2] = [cx + r * Math.cos(s1), cy + r * Math.sin(s1)];
        const mid = (s0 + s1) / 2;
        return { ...d, path: `M${cx},${cy}L${x1},${y1}A${r},${r},0,${la},1,${x2},${y2}Z`, mx: cx + r * .7 * Math.cos(mid), my: cy + r * .7 * Math.sin(mid), col: d.color || PAL[i % PAL.length] };
    });
    const hd = hov != null ? segs[hov] : null;
    return (
        <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <svg width={size} height={size} style={{ flexShrink: 0 }}>
                {segs.map((s, i) => {
                    const act = active === s.key; const isH = hov === i;
                    return <path key={s.key} d={s.path} fill={s.col} stroke={C.white} strokeWidth={act || isH ? 2.5 : 1.5}
                        opacity={active && !act ? 0.32 : 1}
                        transform={isH ? `translate(${(s.mx - cx) * .07},${(s.my - cy) * .07})` : ""}
                        style={{ cursor: onFilter ? "pointer" : "default", transition: "all .15s" }}
                        onClick={() => onFilter && onFilter(act ? null : s.key)}
                        onMouseEnter={() => setHov(i)} onMouseLeave={() => setHov(null)} />;
                })}
                <circle cx={cx} cy={cy} r={ir} fill={C.white} />
                <text x={cx} y={cy - 4} textAnchor="middle" fontSize={hd ? 10 : 13} fontWeight={800} fill={hd ? hd.col : C.p700}>{hd ? hd.count : total}</text>
                <text x={cx} y={cy + 9} textAnchor="middle" fontSize={7} fill={C.n500}>{hd ? hd.key.slice(0, 11) : "Total"}</text>
            </svg>
            <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 3, minWidth: 0 }}>
                {segs.map((s, i) => {
                    const act = active === s.key;
                    return <div key={s.key} onClick={() => onFilter && onFilter(act ? null : s.key)}
                        style={{
                            display: "flex", alignItems: "center", gap: 4, cursor: onFilter ? "pointer" : "default",
                            opacity: active && !act ? .38 : 1, background: act ? `${s.col}14` : "transparent", borderRadius: 4, padding: "2px 4px", transition: "opacity .15s"
                        }}>
                        <span style={{ width: 6, height: 6, borderRadius: 2, background: s.col, flexShrink: 0 }} />
                        <span style={{ fontSize: 9.5, color: act ? s.col : C.n700, fontWeight: act ? 700 : 400, flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.key}</span>
                        <span style={{ fontSize: 10, fontWeight: 700, color: act ? s.col : C.n800 }}>{s.count}</span>
                        <span style={{ fontSize: 8.5, color: C.n400, minWidth: 20, textAlign: "right" }}>{pct(s.count, total)}%</span>
                    </div>;
                })}
            </div>
        </div>
    );
}

/* ══════════════════════════════════════════════════════
   CHART: H-BAR
   FIX #5: supports an optional `label` per data item, used
   for display, while `key` stays the real filter identity
   (e.g. emp_code) used for the active-state comparison and
   the onFilter callback.
══════════════════════════════════════════════════════ */
function HBar({ data, total, onFilter, active, valueFn, compact, maxItems = 15 }) {
    const [showAll, setShowAll] = useState(false);
    if (!data || !data.length) return <div style={{ color: C.n400, fontSize: 11, padding: "8px 0", textAlign: "center" }}>No data</div>;
    const vis = showAll ? data.slice(0, maxItems) : data.slice(0, compact ? 10 : 8);
    const mx = Math.max(...data.map(d => d.count), 1);
    return (
        <div>
            <div style={{ display: "flex", flexDirection: "column", gap: compact ? 3 : 4 }}>
                {vis.map((d, i) => {
                    const w = pct(d.count, mx); const p = pct(d.count, total || mx);
                    const act = active === d.key; const col = d.color || PAL[i % PAL.length];
                    const labelText = d.label || d.key;
                    return <div key={d.key} onClick={() => onFilter && onFilter(act ? null : d.key)}
                        style={{
                            cursor: onFilter ? "pointer" : "default", padding: compact ? "3px 6px" : "5px 7px", borderRadius: 6,
                            border: `1px solid ${act ? col : C.n200}`, background: act ? `${col}12` : C.n100, transition: "all .13s"
                        }}
                        onMouseEnter={e => { if (!act && onFilter) e.currentTarget.style.background = `${col}09`; }}
                        onMouseLeave={e => { if (!act) e.currentTarget.style.background = C.n100; }}>
                        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 3 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 5, minWidth: 0, flex: 1 }}>
                                <span style={{ width: 6, height: 6, borderRadius: 1, background: col, flexShrink: 0 }} />
                                <span style={{ fontSize: compact ? 9.5 : 10.5, fontWeight: act ? 700 : 500, color: act ? col : C.n700, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: 160 }} title={labelText}>{labelText}</span>
                            </div>
                            <div style={{ display: "flex", gap: 5, alignItems: "center", flexShrink: 0 }}>
                                {valueFn && <span style={{ fontSize: 9, color: C.n500 }}>{valueFn(d)}</span>}
                                <span style={{ fontSize: 11, fontWeight: 700, color: act ? col : C.n800, minWidth: 20, textAlign: "right" }}>{d.count}</span>
                                {total > 0 && <span style={{ fontSize: 8.5, color: C.n400, minWidth: 22, textAlign: "right" }}>{p}%</span>}
                            </div>
                        </div>
                        <div style={{ height: compact ? 3 : 4, background: C.n200, borderRadius: 3 }}>
                            <div style={{ width: `${w}%`, height: "100%", background: col, borderRadius: 3, transition: "width .35s" }} />
                        </div>
                    </div>;
                })}
            </div>
            {data.length > vis.length && (
                <button onClick={() => setShowAll(s => !s)} style={{ marginTop: 5, width: "100%", padding: "4px", background: "none", border: `1px dashed ${C.n300}`, borderRadius: 5, fontSize: 9.5, color: C.p500, cursor: "pointer", fontWeight: 600 }}>
                    {showAll ? `▲ Less` : `▼ +${data.length - vis.length} more`}
                </button>
            )}
        </div>
    );
}

/* ══════════════════════════════════════════════════════
   CHART: CLIENT BAR — FIXED
   • Bars always anchored to bottom (alignItems:flex-end)
   • Fixed-height label zone so long names never push bars
   • Proper border-top baseline separating bar from label
══════════════════════════════════════════════════════ */


function ClientBar({ data, onFilter, active }) {
    const containerRef = useRef(null);
    const [chartHeight, setChartHeight] = useState(120);

    const LABEL_H = 36;
    const COUNT_H = 16;
    const COL_W = 44;

    useEffect(() => {
        if (!containerRef.current) return;

        const observer = new ResizeObserver(([entry]) => {
            const h = entry.contentRect.height;
            setChartHeight(Math.max(h - LABEL_H - COUNT_H - 10, 40));
        });

        observer.observe(containerRef.current);

        return () => observer.disconnect();
    }, []);

    if (!data || !data.length) {
        return (
            <div
                style={{
                    color: C.n400,
                    fontSize: 11,
                    textAlign: "center",
                    padding: "10px 0"
                }}
            >
                No data
            </div>
        );
    }

    const top = data.slice(0, 18);
    const mx = Math.max(...top.map(d => d.count), 1);

    return (
        <div
            ref={containerRef}
            style={{
                height: "100%",
                width: "100%",
                overflowX: "auto",
                overflowY: "hidden"
            }}
        >
            <div
                style={{
                    display: "flex",
                    alignItems: "flex-end",
                    height: "100%",
                    gap: 4,
                    minWidth: top.length * (COL_W + 4),
                    padding: "2px"
                }}
            >
                {top.map((d, i) => {
                    const col = d.color || PAL[i % PAL.length];
                    const act = active === d.key;

                    const barH = Math.max(
                        Math.round((d.count / mx) * chartHeight),
                        8
                    );

                    const shortName =
                        d.key.length > 8
                            ? d.key.slice(0, 7) + "…"
                            : d.key;

                    return (
                        <div
                            key={d.key}
                            title={d.key}
                            onClick={() =>
                                onFilter &&
                                onFilter(act ? null : d.key)
                            }
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                justifyContent: "flex-end",
                                alignItems: "center",
                                width: COL_W,
                                flexShrink: 0,
                                cursor: onFilter ? "pointer" : "default",
                                opacity: active && !act ? 0.38 : 1,
                                transition: "opacity .15s",
                                height: "100%"
                            }}
                        >
                            <span
                                style={{
                                    fontSize: 9,
                                    fontWeight: 700,
                                    color: act ? col : C.n600,
                                    marginBottom: 3,
                                    lineHeight: 1
                                }}
                            >
                                {d.count}
                            </span>

                            <div
                                style={{
                                    display: "flex",
                                    alignItems: "flex-end",
                                    height: chartHeight,
                                    width: "100%"
                                }}
                            >
                                <div
                                    style={{
                                        width: "100%",
                                        height: barH,
                                        background: act
                                            ? col
                                            : `${col}cc`,
                                        borderRadius: "4px 4px 0 0",
                                        border: act
                                            ? `2px solid ${col}`
                                            : `1px solid ${col}55`,
                                        boxSizing: "border-box",
                                        transition: "all .2s"
                                    }}
                                />
                            </div>

                            <div
                                style={{
                                    height: LABEL_H,
                                    width: "100%",
                                    display: "flex",
                                    alignItems: "flex-start",
                                    justifyContent: "center",
                                    paddingTop: 5,
                                    borderTop: `1px solid ${C.n200}`
                                }}
                            >
                                <span
                                    style={{
                                        fontSize: 7.5,
                                        color: act ? col : C.n500,
                                        textAlign: "center",
                                        wordBreak: "break-word",
                                        lineHeight: 1.25,
                                        maxWidth: COL_W - 4,
                                        fontWeight: act ? 700 : 400,
                                        display: "-webkit-box",
                                        WebkitLineClamp: 2,
                                        WebkitBoxOrient: "vertical",
                                        overflow: "hidden"
                                    }}
                                >
                                    {shortName}
                                </span>
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

/* ══════════════════════════════════════════════════════
   CHART: TREND LINE — FIXED
   Key fixes:
   1. Both datasets always rendered when sector + type set.
   2. stableKey resets hover to avoid stale ghost dots.
   3. useRef keeps hover in sync with SVG mouse events.
   4. Each dataset is fully independent — one empty array
      will NOT suppress the other.
══════════════════════════════════════════════════════ */
function TrendLine({ datasets, height = 115, stableKey }) {
    const [hov, setHov] = useState(null);
    const hovRef = useRef(null);

    useEffect(() => {
        setHov(null);
        hovRef.current = null;
    }, [stableKey]);

    const W = 460, H = height, pL = 36, pR = 12, pT = 16, pB = 28;

    const validDatasets = (datasets || []).filter(
        ds => Array.isArray(ds?.points) && ds.points.length > 0
    );

    if (!validDatasets.length)
        return (
            <div style={{ height: H, display: "flex", alignItems: "center", justifyContent: "center", color: C.n400, fontSize: 11 }}>
                No trend data
            </div>
        );

    const norm = ds => ({
        ...ds,
        points: ds.points.map(p => ({
            month: p.month || p.label || "",
            value: Number(p.value ?? p.total_tenders ?? p.count ?? 0),
        })),
    });
    const nds = validDatasets.map(norm);

    const allV = nds.flatMap(d => d.points.map(p => p.value));
    const minV = 0; // always anchor to 0
    const maxV = Math.max(...allV, 1);
    const range = Math.max(maxV - minV, 1);

    // Build unified label set from longest dataset
    const labels = nds.reduce(
        (best, ds) => ds.points.length >= best.length ? ds.points.map(p => p.month) : best,
        []
    );
    const maxLen = labels.length;

    const toX = i => pL + (maxLen <= 1 ? (W - pL - pR) / 2 : i * (W - pL - pR) / (maxLen - 1));
    const toY = v => pT + (H - pT - pB) * (1 - (v - minV) / range);

    // Nice grid ticks: pick 4-5 clean integer steps
    const niceMax = Math.ceil(maxV / Math.max(1, Math.ceil(maxV / 5))) * Math.max(1, Math.ceil(maxV / 5));
    const step = Math.max(1, Math.ceil(niceMax / 5));
    const ticks = [];
    for (let v = 0; v <= maxV; v += step) ticks.push(v);
    if (ticks[ticks.length - 1] < maxV) ticks.push(Math.ceil(maxV / step) * step);

    const COLS = [C.p400, C.r500, C.l500, "#e09c13", "#8B5CF6", "#f97316"];

    // Detect identical datasets — offset one slightly for visibility
    const areIdentical = nds.length === 2 &&
        nds[0].points.every((p, i) => p.value === (nds[1].points[i]?.value ?? -1));

    return (
        <div>
            {nds.length > 1 && (
                <div style={{ display: "flex", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
                    {nds.map((d, i) => (
                        <div key={i} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            <span style={{
                                display: "inline-block", width: 18, height: 2.5,
                                background: COLS[i % COLS.length], borderRadius: 2,
                                ...(i === 1 && areIdentical ? { borderTop: "1px dashed", height: 0, borderColor: COLS[i % COLS.length] } : {})
                            }} />
                            <span style={{ fontSize: 9.5, color: C.n600, fontWeight: 600 }}>{d.label}</span>
                            {areIdentical && i === 1 && (
                                <span style={{ fontSize: 8, color: C.n400 }}>(identical values)</span>
                            )}
                        </div>
                    ))}
                </div>
            )}
            <svg viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", display: "block", overflow: "visible" }}>
                <defs>
                    {nds.map((_, i) => (
                        <linearGradient key={i} id={`tg${stableKey || "x"}${i}`} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={COLS[i % COLS.length]} stopOpacity=".18" />
                            <stop offset="100%" stopColor={COLS[i % COLS.length]} stopOpacity=".01" />
                        </linearGradient>
                    ))}
                </defs>

                {/* Grid lines using nice ticks */}
                {ticks.map(v => {
                    const y = toY(v);
                    return (
                        <g key={v}>
                            <line x1={pL} y1={y} x2={W - pR} y2={y} stroke={C.n200} strokeWidth={1} />
                            <text x={pL - 4} y={y + 3} textAnchor="end" fontSize={8} fill={C.n400}>{v}</text>
                        </g>
                    );
                })}

                {/* X-axis labels */}
                {labels.map((l, i) => (
                    <text key={i} x={toX(i)} y={H - pB + 14} textAnchor="middle" fontSize={8.5} fill={C.n400}>{l}</text>
                ))}

                {/* Render each dataset */}
                {nds.map((ds, di) => {
                    const col = COLS[di % COLS.length];

                    // For identical datasets, shift second line up by ~6px so both are visible
                    const yOffset = (areIdentical && di === 1) ? -6 : 0;

                    const pts = ds.points.map((p, i) => ({
                        x: toX(i),
                        y: toY(p.value) + yOffset,
                        rawValue: p.value,
                    }));

                    if (!pts.length) return null;

                    const ld = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x},${p.y}`).join(" ");
                    const baseY = toY(minV);
                    const ad = `${ld}L${pts[pts.length - 1].x},${baseY}L${pts[0].x},${baseY}Z`;
                    const isDashed = areIdentical && di === 1;

                    return (
                        <g key={`ds-${di}-${ds.label}`}>
                            {!isDashed && <path d={ad} fill={`url(#tg${stableKey || "x"}${di})`} />}
                            <path
                                d={ld}
                                fill="none"
                                stroke={col}
                                strokeWidth={2.5}
                                strokeDasharray={isDashed ? "5,3" : undefined}
                                strokeLinejoin="round"
                                strokeLinecap="round"
                            />
                            {pts.map((p, i) => {
                                const isH = hov?.di === di && hov?.i === i;
                                return (
                                    <g key={i}
                                        onMouseEnter={() => { const v = { di, i }; setHov(v); hovRef.current = v; }}
                                        onMouseLeave={() => { setHov(null); hovRef.current = null; }}>
                                        <circle
                                            cx={p.x} cy={p.y} r={isH ? 5.5 : 3.5}
                                            fill={C.white} stroke={col} strokeWidth={2}
                                            style={{ transition: "r .1s", cursor: "default" }}
                                        />
                                        {isH && (
                                            <text x={p.x} y={p.y - 9} textAnchor="middle" fontSize={9.5} fontWeight={700} fill={col}>
                                                {p.rawValue}
                                            </text>
                                        )}
                                    </g>
                                );
                            })}
                        </g>
                    );
                })}
            </svg>
        </div>
    );
}

/* ══════════════════════════════════════════════════════
   UI ATOMS
══════════════════════════════════════════════════════ */
function Card({ title, subtitle, children, style = {}, action }) {
    return (
        <div style={{ background: C.white, borderRadius: 10, border: `1px solid ${C.n200}`, boxShadow: C.sh1, padding: "13px 15px", overflow: "hidden", ...style }}>
            {(title || action) && (
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 11 }}>
                    <div>
                        <span style={{ fontSize: 11.5, fontWeight: 700, color: C.n800 }}>{title}</span>
                        {subtitle && <span style={{ fontSize: 9.5, color: C.n400, marginLeft: 6 }}>{subtitle}</span>}
                    </div>
                    {action}
                </div>
            )}
            {children}
        </div>
    );
}

function Stat({ icon, label, value, sub, subUp, color, bg }) {
    return (
        <div style={{ background: C.white, borderRadius: 10, border: `1px solid ${C.n200}`, boxShadow: C.sh1, padding: "11px 13px", flex: 1, minWidth: 120 }}>
            <div style={{ display: "flex", alignItems: "flex-start", gap: 8 }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: bg || C.p50, display: "flex", alignItems: "center", justifyContent: "center", color: color || C.p500, flexShrink: 0 }}>{icon}</div>
                <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 19, fontWeight: 800, color: C.n900, letterSpacing: -0.5, lineHeight: 1.1 }}>{value}</div>
                    <div style={{ fontSize: 9.5, color: C.n600, fontWeight: 500, marginTop: 1 }}>{label}</div>
                    {sub && <div style={{ display: "flex", alignItems: "center", gap: 2, marginTop: 2 }}>
                        <span style={{ color: subUp ? C.ok : C.err, fontSize: 8.5, display: "flex", alignItems: "center", gap: 1, fontWeight: 700 }}>{subUp && <I.Up />}{sub}</span>
                    </div>}
                </div>
            </div>
        </div>
    );
}

function ProgBar({ value, max, color }) {
    const p = max > 0 ? Math.min(100, Math.round(value / max * 100)) : 0;
    return (
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <div style={{ flex: 1, height: 4, background: C.n200, borderRadius: 3 }}>
                <div style={{ width: `${p}%`, height: "100%", background: color || C.p400, borderRadius: 3, transition: "width .4s" }} />
            </div>
            <span style={{ fontSize: 8.5, fontWeight: 700, color: color || C.p500, minWidth: 24, textAlign: "right" }}>{p}%</span>
        </div>
    );
}

function Loader() {
    return <div style={{ height: 3, background: C.n200, borderRadius: 2, overflow: "hidden", marginBottom: 4 }}>
        <div style={{ height: "100%", background: `linear-gradient(90deg,${C.p400},${C.r500},${C.p400})`, backgroundSize: "200% 100%", animation: "ls 1.2s linear infinite", borderRadius: 2 }} />
    </div>;
}
function ErrBanner() {
    return <div style={{ background: C.errBg, border: `1px solid ${C.errBd}`, borderRadius: 8, padding: "9px 14px", fontSize: 11, color: C.err, display: "flex", alignItems: "center", gap: 6 }}><I.Alert />API not reachable — check VPN / server.</div>;
}
function Empty({ msg = "No data for this view." }) {
    return <div style={{ padding: "26px", textAlign: "center", color: C.n400, fontSize: 12, background: C.white, borderRadius: 10, border: `1px solid ${C.n200}` }}>{msg}</div>;
}

/* ══════════════════════════════════════════════════════
   FIX (bonus, supports #5): ActiveChips now takes the
   REAL lowercase filter keys (matching useFilters' state
   shape) so clicking "×" actually clears the filter. The
   previous version passed capitalised display labels as
   the key, so onRemove("Sector") silently created a stray
   "Sector" property instead of clearing "sector".
══════════════════════════════════════════════════════ */
const CHIP_LABELS = { tl: "TL", emd: "EMD" };
function ActiveChips({ filters, onRemove, onClearAll }) {
    const act = Object.entries(filters).filter(([, v]) => v);
    if (!act.length) return null;
    const labelFor = k => CHIP_LABELS[k] || (k.charAt(0).toUpperCase() + k.slice(1));
    return (
        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center", padding: "6px 0" }}>
            <span style={{ fontSize: 9.5, color: C.n500, fontWeight: 600, display: "flex", alignItems: "center", gap: 3 }}><I.Filter />Filters:</span>
            {act.map(([k, v]) => (
                <span key={k} onClick={() => onRemove(k)}
                    style={{ display: "inline-flex", alignItems: "center", gap: 3, background: C.p50, color: C.p600, border: `1px solid ${C.p200}`, borderRadius: 20, padding: "2px 9px", fontSize: 9.5, fontWeight: 600, cursor: "pointer" }}>
                    <span style={{ color: C.n400, fontSize: 9 }}>{labelFor(k)}:</span>{String(v)}<span style={{ opacity: .5, marginLeft: 1 }}>×</span>
                </span>
            ))}
            <span onClick={onClearAll} style={{ fontSize: 9.5, color: C.n400, cursor: "pointer", textDecoration: "underline" }}>Clear all</span>
        </div>
    );
}

function useFilters(keys) {
    const init = Object.fromEntries(keys.map(k => [k, null]));
    const [f, setF] = useState(init);
    const set = (k, v) => setF(p => ({ ...p, [k]: p[k] === v ? null : v }));
    const remove = k => setF(p => ({ ...p, [k]: null }));
    const clear = () => setF(init);
    return { f, set, remove, clear };
}

function InfoBanner({ title, items, tone }) {
    if (!items?.length) return null;
    const toneBg = tone === "amber" ? "#fffaf0" : "#f0f7ff";
    const toneBd = tone === "amber" ? C.warnBd : C.p100;
    const toneTxt = tone === "amber" ? C.warn : C.p700;
    return (
        <div style={{ background: toneBg, border: `1px solid ${toneBd}`, borderRadius: 9, padding: "8px 14px", display: "flex", flexWrap: "wrap", gap: 7, alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 5, color: toneTxt, flexShrink: 0 }}>
                <I.Info /><span style={{ fontSize: 10, fontWeight: 700, color: toneTxt }}>{title || "Insights"}</span>
            </div>
            {items.map((it, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 4, background: C.white, border: `1px solid ${C.n200}`, borderRadius: 6, padding: "3px 9px" }}>
                    <span style={{ fontSize: 11, fontWeight: 700, color: it.color || C.p500 }}>{it.value}</span>
                    <span style={{ fontSize: 9.5, color: C.n600 }}>{it.label}</span>
                </div>
            ))}
        </div>
    );
}

/* ══════════════════════════════════════════════════════
   SECTOR + TYPE + TREND ROW — FIXED
   Both sector and type datasets are always built
   independently. Neither absence suppresses the other.
══════════════════════════════════════════════════════ */
function STTRow({ analytics, trend, activeSector, activeType, onSector, onType }) {
    const sectorData = useMemo(() => Object.entries(analytics?.sector_wise_counts || {})
        .map(([k, c], i) => ({ key: k, count: c, color: SEC_COL[k] || PAL[i] }))
        .sort((a, b) => b.count - a.count), [analytics]);

    const typeData = useMemo(() => Object.entries(analytics?.tender_type_counts || {})
        .map(([k, c], i) => ({ key: k, count: c, color: PAL[i % PAL.length] }))
        .sort((a, b) => b.count - a.count), [analytics]);

    const trendDatasets = useMemo(() => {
        const ds = [];

        if (activeSector) {
            const raw = trend?.sector_trend?.[activeSector] || [];
            const pts = raw.map(p => ({
                month: p.month || p.label || "",
                value: Number(p.value ?? p.total_tenders ?? p.count ?? 0),
            }));
            if (pts.length > 0) ds.push({ label: activeSector, points: pts });
        }

        if (activeType) {
            const raw = trend?.type_trend?.[activeType] || [];
            const pts = raw.map(p => ({
                month: p.month || p.label || "",
                value: Number(p.value ?? p.total_tenders ?? p.count ?? 0),
            }));
            if (pts.length > 0) ds.push({ label: activeType, points: pts });
        }

        // Overall fallback only when nothing selected
        if (ds.length === 0 && trend?.monthly_breakdown?.length) {
            ds.push({
                label: "Overall",
                points: trend.monthly_breakdown.map(m => ({
                    month: m.month || "",
                    value: Number(m.total_tenders ?? 0),
                })),
            });
        }

        return ds;
    }, [activeSector, activeType, trend]);

    // Include dataset count in key so TrendLine resets hover when count changes
    const trendKey = `${activeSector || "_"}__${activeType || "_"}__${trendDatasets.length}`;

    const secTotal = sectorData.reduce((a, d) => a + d.count, 0);
    const typTotal = typeData.reduce((a, d) => a + d.count, 0);
    const trendTitle = activeSector && activeType
        ? `${activeSector} + ${activeType}`
        : activeSector ? activeSector
            : activeType ? activeType
                : "Trend Overview";

    return (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
            <Card title="Sector Distribution" subtitle={`${secTotal} · click to filter + trend`}>
                <HBar data={sectorData} total={secTotal} onFilter={onSector} active={activeSector} />
            </Card>
            <Card title="Tender Type Distribution" subtitle={`${typTotal} · click to filter + trend`}>
                <HBar data={typeData} total={typTotal} onFilter={onType} active={activeType} />
            </Card>
            <Card title={`Trend — ${trendTitle}`} subtitle="6 months">
                <TrendLine datasets={trendDatasets} height={350} stableKey={trendKey} />
                {(activeSector || activeType) && (
                    <div style={{ marginTop: 5, display: "flex", gap: 5, flexWrap: "wrap" }}>
                        {activeSector && <span style={{ fontSize: 9, background: C.p50, color: C.p500, border: `1px solid ${C.p100}`, borderRadius: 4, padding: "1px 6px", fontWeight: 600 }}>{activeSector}</span>}
                        {activeType && <span style={{ fontSize: 9, background: "#fffbeb", color: C.warn, border: `1px solid ${C.warnBd}`, borderRadius: 4, padding: "1px 6px", fontWeight: 600 }}>{activeType}</span>}
                    </div>
                )}
            </Card>
        </div>
    );
}

/* ══════════════════════════════════════════════════════
   TENDER TABLE
   FIX #4: Export now fetches every page that matches the
   tab's current filters from the API (not just what's
   currently loaded/paginated on screen) and writes a
   single .xlsx file via the `xlsx` package.
══════════════════════════════════════════════════════ */
function TTable({ tenders, extraCols, onClient, activeClient, onState, activeState, exportParams, exportFilename = "tenders.xlsx", exportEndpoint = API_PATH }) {
    const [page, setPage] = useState(1);
    const [modal, setModal] = useState(null);
    const [exporting, setExporting] = useState(false);
    const [exportProgress, setExportProgress] = useState(null);
    const PP = 15;
    useEffect(() => setPage(1), [tenders]);
    const pages = Math.ceil(tenders.length / PP);
    const rows = tenders.slice((page - 1) * PP, page * PP);
    const hdrs = ["#", "ID", "Name", "Sector", "Type", "Client", "State", "Days", "EMD", "Role", "TLs", ...(extraCols || []).map(c => c.header), ""];

    async function handleExport() {
        if (exporting) return;
        setExporting(true);
        setExportProgress({ page: 0, total: 1 });
        try {
            const all = exportParams
                ? await fetchAllTenders(exportParams, (p, t) => setExportProgress({ page: p, total: t }), exportEndpoint)
                : [];
            downloadTendersExcel(all.length ? all : tenders, exportFilename);
        } catch (e) {
            console.warn("export failed, falling back to on-screen rows", e);
            downloadTendersExcel(tenders, exportFilename);
        } finally {
            setExporting(false);
            setExportProgress(null);
        }
    }

    return (
        <div style={{ background: C.white, borderRadius: 10, border: `1px solid ${C.n200}`, boxShadow: C.sh1, overflow: "hidden" }}>
            <div style={{ padding: "9px 14px", borderBottom: `1px solid ${C.n200}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <span style={{ fontSize: 11.5, fontWeight: 700, color: C.n800 }}>Tender List <span style={{ fontSize: 10, fontWeight: 400, color: C.n500 }}>({tenders.length})</span></span>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    {(onClient || onState) && <span style={{ fontSize: 9, color: C.n500 }}>Click client/state to filter</span>}
                    <button onClick={handleExport} disabled={exporting}
                        style={{ display: "flex", alignItems: "center", gap: 3, padding: "4px 9px", background: exporting ? C.n300 : C.p500, color: C.white, border: "none", borderRadius: 5, fontSize: 9.5, fontWeight: 600, cursor: exporting ? "default" : "pointer" }}>
                        <I.Export />
                        {exporting
                            ? `Exporting${exportProgress ? ` ${exportProgress.page}/${exportProgress.total}` : "…"}`
                            : "Export All"}
                    </button>
                </div>
            </div>
            <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                        <tr style={{ background: C.n150 }}>
                            {hdrs.map(h => <th key={h} style={{ padding: "6px 9px", textAlign: "left", fontWeight: 600, color: C.n600, borderBottom: `1px solid ${C.n200}`, whiteSpace: "nowrap", fontSize: 9 }}>{h}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((t, i) => {
                            const dl = t.days_left ?? 0;
                            const tlN = tlName(t);
                            return (
                                <tr key={t.id} style={{ borderBottom: `1px solid ${C.n200}`, background: i % 2 === 0 ? C.n100 : C.white, transition: "background .08s" }}
                                    onMouseEnter={e => e.currentTarget.style.background = C.p50}
                                    onMouseLeave={e => e.currentTarget.style.background = i % 2 === 0 ? C.n100 : C.white}>
                                    <td style={{ padding: "5px 9px", fontSize: 8.5, color: C.n400 }}>{(page - 1) * PP + i + 1}</td>
                                    <td style={{ padding: "5px 9px", fontFamily: "monospace", fontSize: 8, color: C.p400, fontWeight: 700, whiteSpace: "nowrap" }}>{t.tender_id}</td>
                                    <td style={{ padding: "5px 9px", maxWidth: 200, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", fontSize: 9.5, color: C.n800 }} title={t.tender_name}>{t.tender_name}</td>
                                    <td style={{ padding: "5px 9px" }}><span style={{ background: C.p50, color: SEC_COL[t.sector] || C.p500, borderRadius: 4, padding: "2px 5px", fontSize: 8, fontWeight: 700 }}>{t.sector}</span></td>
                                    <td style={{ padding: "5px 9px", color: C.n600, fontSize: 8.5, whiteSpace: "nowrap" }}>{t.tender_type_value || t.tender_type}</td>
                                    <td style={{ padding: "5px 9px" }}>
                                        <span onClick={() => onClient && onClient(activeClient === t.client ? null : t.client)}
                                            style={{ fontSize: 8.5, fontWeight: 600, color: activeClient === t.client ? C.p500 : C.n700, cursor: onClient ? "pointer" : "default", textDecoration: onClient ? "underline" : "none" }}>{t.client}</span>
                                    </td>
                                    <td style={{ padding: "5px 9px" }}>
                                        <span onClick={() => onState && onState(activeState === t.state ? null : t.state)}
                                            style={{ fontSize: 8.5, color: activeState === t.state ? C.p500 : C.n600, cursor: onState ? "pointer" : "default", textDecoration: onState ? "underline" : "none" }}>{t.state}</span>
                                    </td>
                                    <td style={{ padding: "5px 9px" }}><span style={{ background: dBg(dl), color: dC(dl), borderRadius: 4, padding: "2px 5px", fontSize: 8, fontWeight: 700 }}>{dl}d</span></td>
                                    <td style={{ padding: "5px 9px", color: (t.emd_amount || 0) === 0 ? C.n400 : C.p600, fontSize: 8.5 }}>{(t.emd_amount || 0) === 0 ? "Free" : fmtL(t.emd_amount)}</td>
                                    <td style={{ padding: "5px 9px" }}>
                                        {t.participating_as && <span style={{ fontSize: 8, fontWeight: 700, padding: "2px 5px", borderRadius: 4, background: C.r100, color: C.r500 }}>{t.participating_as}</span>}
                                    </td>
                                    <td style={{ padding: "5px 9px", color: C.n500, fontSize: 8.5, whiteSpace: "nowrap" }} title={tlN}>{tlN?.split(",").slice(0, 2).join(",")}</td>
                                    {(extraCols || []).map(c => <td key={c.header} style={{ padding: "5px 9px", fontSize: 8.5, color: C.n700 }}>{c.render(t)}</td>)}
                                    <td style={{ padding: "5px 9px" }}>
                                        <button onClick={() => setModal(t)} style={{ display: "flex", alignItems: "center", gap: 2, padding: "3px 6px", background: C.p50, color: C.p500, border: `1px solid ${C.p200}`, borderRadius: 5, fontSize: 8, fontWeight: 600, cursor: "pointer" }}><I.Eye />View</button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
                {rows.length === 0 && <div style={{ padding: "20px", textAlign: "center", color: C.n400, fontSize: 11 }}>No tenders match</div>}
            </div>
            {pages > 1 && (
                <div style={{ padding: "7px 14px", borderTop: `1px solid ${C.n200}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 9.5, color: C.n500 }}>Page {page} / {pages}</span>
                    <div style={{ display: "flex", gap: 3 }}>
                        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ padding: "3px 7px", border: `1px solid ${C.n200}`, borderRadius: 5, background: page === 1 ? C.n150 : C.white, color: page === 1 ? C.n400 : C.n700, cursor: page === 1 ? "default" : "pointer", fontSize: 10 }}>‹</button>
                        {Array.from({ length: Math.min(5, pages) }, (_, i) => { const pg = Math.max(1, Math.min(pages - 4, page - 2)) + i; return pg <= pages ? <button key={pg} onClick={() => setPage(pg)} style={{ width: 25, height: 23, border: `1px solid ${page === pg ? C.p400 : C.n200}`, borderRadius: 5, background: page === pg ? C.p500 : C.white, color: page === pg ? C.white : C.n700, cursor: "pointer", fontSize: 9.5, fontWeight: page === pg ? 700 : 400 }}>{pg}</button> : null; })}
                        <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages} style={{ padding: "3px 7px", border: `1px solid ${C.n200}`, borderRadius: 5, background: page === pages ? C.n150 : C.white, color: page === pages ? C.n400 : C.n700, cursor: page === pages ? "default" : "pointer", fontSize: 10 }}>›</button>
                    </div>
                </div>
            )}
            {modal && <TModal t={modal} onClose={() => setModal(null)} />}
        </div>
    );
}

function TModal({ t, onClose }) {
    const dl = t.days_left ?? 0;
    const tlN = tlName(t);
    return (
        <div style={{ position: "fixed", inset: 0, background: "rgba(12,35,64,.55)", zIndex: 9999, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}
            onClick={e => e.target === e.currentTarget && onClose()}>
            <div style={{ background: C.white, borderRadius: 14, width: "100%", maxWidth: 560, maxHeight: "88vh", overflow: "auto", boxShadow: "0 20px 60px rgba(12,35,64,.28)" }}>
                <div style={{ padding: "13px 17px", background: `linear-gradient(135deg,${C.p800},${C.p600})`, borderRadius: "14px 14px 0 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <div>
                        <div style={{ fontSize: 12, fontWeight: 800, color: C.white }}>Tender Details</div>
                        <div style={{ fontSize: 8.5, color: "rgba(255,255,255,.5)", marginTop: 2, fontFamily: "monospace" }}>{t.tender_id}</div>
                    </div>
                    <button onClick={onClose} style={{ background: "rgba(255,255,255,.15)", border: "none", borderRadius: 6, width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: C.white }}><I.X /></button>
                </div>
                <div style={{ padding: "13px 17px" }}>
                    <div style={{ background: C.p50, borderRadius: 8, padding: "8px 11px", marginBottom: 11, border: `1px solid ${C.p100}` }}>
                        <div style={{ fontSize: 10.5, fontWeight: 600, color: C.n800, lineHeight: 1.5 }}>{t.tender_name}</div>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 5 }}>
                        {[["Sector", t.sector], ["Type", t.tender_type_value || t.tender_type], ["Client", t.client], ["State", t.state],
                        ["Participating As", t.participating_as || "—"], ["Lead Partner", t.lead_partner || "—"],
                        ["Cost", fmtL(t.cost_amount ?? t.cost_of_rfp)], ["EMD", (t.emd_amount || 0) === 0 ? "No EMD" : fmtL(t.emd_amount)],
                        ["Tech Score", t.tech_score || "—"], ["Responsive", t.responsive_status || "—"],
                        ["Submission", t.submission_date?.slice(0, 10) || "—"], ["TLs", tlN],
                        ["Finalized", t.is_finalized ? "Yes" : "No"], ["We are L1", t.are_we_l1 ? "Yes ✓" : "No"],
                        ["Stage", t._stage || "—"], ["Consult. Fee", t.consultancy_fees ? `${t.consultancy_fees}%` : "—"]
                        ].map(([k, v]) => (
                            <div key={k} style={{ background: C.n100, borderRadius: 6, padding: "5px 9px", border: `1px solid ${C.n200}` }}>
                                <div style={{ fontSize: 7.5, color: C.n500, fontWeight: 600, textTransform: "uppercase", letterSpacing: .5, marginBottom: 2 }}>{k}</div>
                                <div style={{ fontSize: 10, fontWeight: 700, color: C.n800 }}>{String(v)}</div>
                            </div>
                        ))}
                    </div>
                    <div style={{ marginTop: 9, padding: "7px 11px", background: dBg(dl), borderRadius: 7, border: `1px solid ${dBd(dl)}` }}>
                        <span style={{ fontSize: 11, fontWeight: 700, color: dC(dl) }}>
                            {dl <= 3 ? "⚠ Urgent" : dl <= 7 ? "📅 Due Soon" : "✓ On Track"} — {dl} days · {t.submission_date?.slice(0, 10) || "TBD"}
                        </span>
                    </div>
                </div>
            </div>
        </div>
    );
}

/* ══════════════════════════════════════════════════════
   PIPELINE STRIP
══════════════════════════════════════════════════════ */
function PipelineStrip({ funnel, activeTab, setTab }) {
    const f = funnel || {};
    const f14 = f.tab1_to_tab4 || {}; const f49 = f.tab4_to_tab9 || {};
    const f95 = f.tab9_to_tab5 || {}; const f511 = f.tab5_to_tab11 || {}; const f1112 = f.tab11_to_tab12 || {};
    const steps = [
        { key: "new", label: "New Tenders", val: f14.total_eligible, cr: null },
        { key: "new_final", label: "→ Finalized", val: f14.moved_to_tab4, cr: f14.conversion_rate_pct },
        { key: "final_bid", label: "→ Bid Prep", val: f49.moved_to_tab9, cr: f49.conversion_rate_pct },
        { key: "bid_submit", label: "→ Submitted", val: f95.moved_to_tab5, cr: f95.conversion_rate_pct },
        { key: "tech_open", label: "→ Tech Open", val: f511.moved_to_tab11, cr: f511.conversion_rate_pct },
        { key: "fin_open", label: "→ Financial", val: f1112.moved_to_tab12, cr: f1112.conversion_rate_pct },
    ];
    return (
        <div style={{ background: `linear-gradient(90deg,${C.p800},${C.p600})`, padding: "8px 24px", display: "flex", alignItems: "center", overflowX: "auto" }}>
            {steps.map((s, i) => {
                const act = activeTab === s.key;
                const crCol = s.cr == null ? "rgba(255,255,255,.5)" : s.cr >= 60 ? "#6eddb8" : s.cr >= 30 ? "#ffd47a" : "#f5a3a3";
                return (
                    <div key={s.key} style={{ display: "flex", alignItems: "center" }}>
                        {i > 0 && <span style={{ color: "rgba(255,255,255,.25)", fontSize: 17, margin: "0 1px" }}>›</span>}
                        <button onClick={() => setTab(s.key)}
                            style={{ background: act ? "rgba(255,255,255,.18)" : "transparent", border: act ? "1px solid rgba(255,255,255,.3)" : "1px solid transparent", borderRadius: 7, cursor: "pointer", padding: "5px 10px", transition: "all .15s", textAlign: "left" }}>
                            <div style={{ fontSize: 8, color: "rgba(255,255,255,.5)", marginBottom: 1, whiteSpace: "nowrap" }}>{s.label}</div>
                            <div style={{ fontSize: 16, fontWeight: 800, color: C.white, letterSpacing: -.5 }}>{s.val ?? "—"}</div>
                            {i > 0 && s.cr != null && (
                                <div style={{ display: "flex", alignItems: "center", gap: 3, marginTop: 1 }}>
                                    <div style={{ height: 2, background: "rgba(255,255,255,.15)", borderRadius: 2, flex: 1, minWidth: 28 }}>
                                        <div style={{ width: `${Math.min(s.cr, 100)}%`, height: "100%", background: crCol, borderRadius: 2 }} />
                                    </div>
                                    <span style={{ fontSize: 7.5, color: "rgba(255,255,255,.75)", fontWeight: 700, flexShrink: 0 }}>{s.cr}%</span>
                                </div>
                            )}
                        </button>
                    </div>
                );
            })}
        </div>
    );
}

/* ══════════════════════════════════════════════════════
   TABS META
   The "emd" tab is intentionally separated with a divider
   in the tab strip (see App below) because it walks a
   different lifecycle (EMD instrument tracking) rather
   than the bid-decision pipeline the first six tabs share.
══════════════════════════════════════════════════════ */
const TABS = [
    {
        key: "period_report", label: "Period Report",
        info: "Tenders added in a chosen period: how many moved to Final, Bid Prep and Submit, where they are now, and which expired, were closed or cancelled."
    },
    { key: "weekly_bidprep", label: "Weekly Bid Prep Report", info: "Week-wise report (Mon–Sun) of tenders in bid prep and due that week…" },
    { key: "new", label: "New Tenders", info: "Newly added tenders — no bid decision yet." },
    { key: "new_final", label: "New → Final", info: "Tenders where bid decision was taken (finalized). Tracks 1→4 conversion." },
    { key: "final_bid", label: "Final → Bid Prep", info: "Finalized tenders in active bid preparation. Tracks 4→9 conversion." },
    { key: "bid_submit", label: "Bid Prep → Submit", info: "Bids prepared and submitted to clients. Tracks 9→5 conversion." },
    { key: "tech_open", label: "Tech Open", info: "Submitted bids opened for technical evaluation. Tracks 5→11 conversion." },
    { key: "fin_open", label: "Financial Open", info: "Technically cleared bids in financial evaluation. Tracks 11→12 conversion." },
    { key: "emd", label: "EMD Tracker", info: "Tracks Earnest Money Deposit (EMD) instruments through their own money-lifecycle — Upcoming → Created → Submitted → BG Created → Matured/Pending → Collected — independent of the bid-decision pipeline above.", accent: true },
    // { key: "technical_result_analytics", label: "Technical Result Analytics", info: "Deep-dive on the Tech Open stage — score distribution, sector/TL performance, EMD mix and how score correlates with becoming L1." },
    // {
    //     key: "financial_result_analytics", label: "Financial Result Analytics",
    //     info: "Price ranking, gap to L1, margin over L2, bid vs estimate, competitor pricing and tech-vs-price linkage for tenders in financial evaluation."
    // },
];

function ClientStateRow({ analytics, activeClient, activeState, onClient, onState }) {
    const clientData = useMemo(() => Object.entries(analytics?.client_counts || {}).filter(([k]) => k)
        .map(([k, c], i) => ({ key: k, count: c, color: PAL[i % PAL.length] })).sort((a, b) => b.count - a.count), [analytics]);
    const stateData = useMemo(() => Object.entries(analytics?.state_counts || {}).filter(([k]) => k)
        .map(([k, c], i) => ({ key: k, count: c, color: PAL[i % PAL.length] })).sort((a, b) => b.count - a.count), [analytics]);
    return (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, position: "relative" }}>
            <Card title="Client Distribution" subtitle="Click bar to filter">
                <HBar
                    data={clientData}
                    total={clientData.reduce((a, d) => a + d.count, 0)}
                    onFilter={onClient}
                    active={activeClient}
                    compact
                />
            </Card>
            <Card title="State Distribution" subtitle="Click to filter">
                <HBar data={stateData} total={stateData.reduce((a, d) => a + d.count, 0)} onFilter={onState} active={activeState} compact />
            </Card>
        </div>
    );
}

/* ══════════════════════════════════════════════════════
   TL CARD — FIXED  (FIX #5)
   `key` is now the real emp_code (the actual filtering
   identity, and what each tender's tl_details.assigned_tls
   entries store as their `name` field). `label` is the
   resolved human-readable name used purely for display.
   Accepts onFilter/active so it can participate in the
   filter system like every other chart.
══════════════════════════════════════════════════════ */
function TLCard({ tlSource, analytics, title = "TL-wise Distribution", subtitle, onFilter, active }) {
    const data = useMemo(() => {
        const tlMap = buildTLMap(analytics);
        if (!tlSource) return [];

        if (Array.isArray(tlSource)) {
            return tlSource
                .map((t, i) => {
                    const empCode = t.emp_code || t.name || String(i);
                    const displayName = tlMap[empCode] || t.name || empCode;
                    return { key: empCode, label: displayName, count: t.tender_count || t.tender_total || 0, color: PAL[i % PAL.length] };
                })
                .sort((a, b) => b.count - a.count);
        }

        return Object.entries(tlSource)
            .map(([k, c], i) => {
                const displayName = tlMap[k] || k;
                return { key: k, label: displayName, count: typeof c === "number" ? c : c.tender_count || c.tender_total || 0, color: PAL[i % PAL.length] };
            })
            .sort((a, b) => b.count - a.count);
    }, [tlSource, analytics]);

    return (
        <Card title={title} subtitle={subtitle}>
            <HBar data={data} total={data.reduce((a, d) => a + d.count, 0)} onFilter={onFilter} active={active} />
        </Card>
    );
}

/* ══════════════════════════════════════════════════════
   TAB 1: NEW TENDERS
══════════════════════════════════════════════════════ */
function NewTab({ gp, onFunnel }) {
    const { f, set, remove, clear } = useFilters(["sector", "type", "emd", "client", "state"]);
    const params = { ...gp, tab: "1", sector: f.sector, tender_type: f.type, emd_filter: f.emd, client: f.client, state: f.state };
    const { data, loading, error } = useDashboard(params);

    // FIX #1: report this tab's funnel data up so the parent doesn't
    // need its own separate (duplicate) root fetch for the pipeline strip.
    useEffect(() => { if (data?.funnel && onFunnel) onFunnel(data.funnel); }, [data, onFunnel]);

    const T = data?.tenders || [];
    const A = data?.analytics || {};
    const t1 = data?.tab1_analytics || {};
    const mainTrend = data?.trend_overview || {};

    // FIX #1: only fetch the dedicated "unfiltered trend" call when a
    // filter is actually active — otherwise it's an exact duplicate of
    // the main call above.
    const hasFilters = !!(f.sector || f.type || f.emd || f.client || f.state);
    const { trend: unfilteredTrend } = useUnfilteredTrend(params.tab, gp.filter, !hasFilters);

    const trend = {
        ...mainTrend,
        sector_trend: unfilteredTrend?.sector_trend || mainTrend.sector_trend,
        type_trend: unfilteredTrend?.type_trend || mainTrend.type_trend,
        monthly_breakdown: mainTrend.monthly_breakdown || unfilteredTrend?.monthly_breakdown,
    };
    const f14 = data?.funnel?.tab1_to_tab4 || {};
    const avgDays = f14.timeline?.avg_days ?? f14.avg_days_tab1_to_tab4 ?? 0;
    const emdData = useMemo(() => Object.entries(A.emd_analytics || {}).map(([k, c], i) => ({ key: k, count: c, color: PAL[i] })).filter(d => d.count > 0), [A]);

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {loading && <Loader />}{error && <ErrBanner />}
            {f14.total_eligible && <InfoBanner title="Pipeline Snapshot" items={[
                { label: "Total Eligible", value: f14.total_eligible, color: C.p500 },
                { label: "Finalized", value: f14.moved_to_tab4, color: C.ok },
                { label: "Still in New", value: f14.still_in_tab1_or_earlier, color: C.warn },
                { label: "Avg Days to Final", value: `${avgDays}d`, color: C.r500 },
                { label: "Conversion", value: `${f14.conversion_rate_pct ?? 0}%`, color: (f14.conversion_rate_pct ?? 0) >= 50 ? C.ok : C.warn },
            ]} />}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <Stat icon={<I.Doc />} label="Tenders in Tab" value={t1.total ?? T.length} color={C.p500} bg={C.p50} />
                <Stat icon={<I.Star />} label="Special" value={t1.special_tenders ?? 0} color={C.r500} bg={C.r100} />
                <Stat icon={<I.Doc />} label="Normal" value={t1.normal_tenders ?? 0} color={C.n700} bg={C.n150} />
                <Stat icon={<I.Check />} label="With Consult. Fees" value={t1.with_consultancy_fees ?? 0} color={C.ok} bg={C.okBg} />
                <Stat icon={<I.Clock />} label="Without Consult. Fees" value={t1.without_consultancy_fees ?? 0} color={C.warn} bg={C.warnBg} />
                <Stat icon={<I.Money />} label="Total Consult. Cost" value={t1.total_consultancy_cost ? `₹${t1.total_consultancy_cost.toFixed(1)}Cr` : "—"} color={C.p600} bg={C.p50} />
            </div>
            <ActiveChips filters={{ sector: f.sector, type: f.type, emd: f.emd, client: f.client, state: f.state }} onRemove={remove} onClearAll={clear} />
            <STTRow analytics={A} trend={trend} activeSector={f.sector} activeType={f.type} onSector={v => set("sector", v)} onType={v => set("type", v)} />
            <ClientStateRow analytics={A} activeClient={f.client} activeState={f.state} onClient={v => set("client", v)} onState={v => set("state", v)} />
            <Card title="EMD Distribution" subtitle="Click to filter">
                <Donut data={emdData} size={130} onFilter={v => set("emd", v)} active={f.emd} />
            </Card>
            {T.length > 0 ? <TTable tenders={T} onClient={v => set("client", v)} activeClient={f.client} onState={v => set("state", v)} activeState={f.state} exportParams={params} exportFilename="new_tenders.xlsx" /> : !loading && <Empty />}
        </div>
    );
}

/* ══════════════════════════════════════════════════════
   TAB 2: NEW → FINAL
══════════════════════════════════════════════════════ */
function NewFinalTab({ gp, onFunnel }) {
    const { f, set, remove, clear } = useFilters(["sector", "type", "client", "state", "tl"]);
    const params = { ...gp, tab: "4", sector: f.sector, tender_type: f.type, client: f.client, state: f.state, [TL_QUERY_PARAM]: f.tl };
    const { data, loading, error } = useDashboard(params);
    useEffect(() => { if (data?.funnel && onFunnel) onFunnel(data.funnel); }, [data, onFunnel]);

    const rawT = data?.tenders || [];
    // FIX #5: client-side safety net so the table always reflects the TL
    // filter by emp_code, even if the backend ignores the query param above.
    const T = useMemo(() => f.tl ? rawT.filter(t => (t.tl_details?.assigned_tls || []).some(a => a.name === f.tl)) : rawT, [rawT, f.tl]);

    const A = data?.analytics || {};
    const mainTrend = data?.trend_overview || {};
    const hasFilters = !!(f.sector || f.type || f.client || f.state || f.tl);
    const { trend: unfilteredTrend } = useUnfilteredTrend(params.tab, gp.filter, !hasFilters);
    const trend = {
        ...mainTrend,
        sector_trend: unfilteredTrend?.sector_trend || mainTrend.sector_trend,
        type_trend: unfilteredTrend?.type_trend || mainTrend.type_trend,
        monthly_breakdown: mainTrend.monthly_breakdown || unfilteredTrend?.monthly_breakdown,
    };
    const f14 = data?.funnel?.tab1_to_tab4 || {};
    const avgDays = f14.timeline?.avg_days ?? f14.avg_days_tab1_to_tab4 ?? 0;
    const cr = f14.conversion_rate_pct ?? 0;
    const emdData = useMemo(() => Object.entries(A.emd_analytics || {}).map(([k, c], i) => ({ key: k, count: c, color: PAL[i] })).filter(d => d.count > 0), [A]);
    const paData = useMemo(() => Object.entries(A.participating_as_analytics || {}).map(([k, c], i) => ({ key: k, count: c, color: PAL[i] })).filter(d => d.count > 0), [A]);
    const partnerData = useMemo(() => Object.entries(A.partner_wise_analytics || {}).filter(([k]) => k).map(([k, c], i) => ({ key: k, count: c, color: PAL[i % PAL.length] })).sort((a, b) => b.count - a.count), [A]);
    const tlSrc = A?.tl_summary?.assigned_tls || [];
    const finBuckets = [{ l: "Same Day", r: [0, 0] }, { l: "1–3 Days", r: [1, 3] }, { l: "4–7 Days", r: [4, 7] }, { l: "8–14 Days", r: [8, 14] }, { l: ">14 Days", r: [15, 999] }];

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {loading && <Loader />}{error && <ErrBanner />}
            <InfoBanner title="New → Final (1→4)" items={[
                { label: "Total Eligible", value: f14.total_eligible ?? 0, color: C.p500 },
                { label: "Finalized", value: f14.moved_to_tab4 ?? T.length, color: C.ok },
                { label: "Still in New", value: f14.still_in_tab1_or_earlier ?? 0, color: C.warn },
                { label: "Avg Days", value: `${avgDays}d`, color: C.r500 },
                { label: "Conversion", value: `${cr}%`, color: cr >= 50 ? C.ok : C.warn },
            ]} />
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <Stat icon={<I.Doc />} label="Total Eligible" value={f14.total_eligible ?? 0} color={C.p500} bg={C.p50} />
                <Stat icon={<I.Check />} label="Finalized" value={f14.moved_to_tab4 ?? T.length} sub={`${cr}% conv.`} subUp={cr > 50} color={C.ok} bg={C.okBg} />
                <Stat icon={<I.Clock />} label="Still in New" value={f14.still_in_tab1_or_earlier ?? 0} color={C.warn} bg={C.warnBg} />
                <Stat icon={<I.Clock />} label="Avg Days" value={`${avgDays}d`} color={C.p600} bg={C.p50} />
                <Stat icon={<I.Star />} label="Conversion" value={`${cr}%`} color={cr >= 50 ? C.ok : C.warn} bg={cr >= 50 ? C.okBg : C.warnBg} />
            </div>
            <ActiveChips filters={{ sector: f.sector, type: f.type, client: f.client, state: f.state, tl: f.tl ? tlDisplayLabel(f.tl, A) : null }} onRemove={remove} onClearAll={clear} />
            <STTRow analytics={A} trend={trend} activeSector={f.sector} activeType={f.type} onSector={v => set("sector", v)} onType={v => set("type", v)} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                <Card title="Participating As"><Donut data={paData} size={120} /></Card>
                <Card title="Partner Companies"><HBar data={partnerData} total={partnerData.reduce((a, d) => a + d.count, 0)} compact /></Card>
                <TLCard tlSource={tlSrc} analytics={A} title="TL-wise Distribution" onFilter={v => set("tl", v)} active={f.tl} />
            </div>
            <ClientStateRow analytics={A} activeClient={f.client} activeState={f.state} onClient={v => set("client", v)} onState={v => set("state", v)} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Card title="EMD Distribution"><Donut data={emdData} size={120} /></Card>
                <Card title="Finalization Timeline" subtitle="Days from new → finalized">
                    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {finBuckets.map((b, i) => {
                            const cnt = T.filter(t => { const d = t.days_to_finalize ?? t.tab1_to_tab4_days ?? 0; return d >= b.r[0] && d <= b.r[1]; }).length;
                            return <div key={b.l}>
                                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                                    <span style={{ fontSize: 10, color: C.n700 }}>{b.l}</span>
                                    <div style={{ display: "flex", gap: 7 }}><span style={{ fontSize: 10.5, fontWeight: 700, color: C.n800 }}>{cnt}</span><span style={{ fontSize: 9, color: C.n400 }}>{pct(cnt, T.length)}%</span></div>
                                </div>
                                <ProgBar value={cnt} max={T.length || 1} color={PAL[i]} />
                            </div>;
                        })}
                    </div>
                </Card>
            </div>
            {T.length > 0 ? <TTable tenders={T} onClient={v => set("client", v)} activeClient={f.client} onState={v => set("state", v)} activeState={f.state} exportParams={params} exportFilename="new_to_final.xlsx" /> : !loading && <Empty />}
        </div>
    );
}

/* ══════════════════════════════════════════════════════
   TAB 3: FINAL → BID PREP
══════════════════════════════════════════════════════ */
function FinalBidTab({ gp, onFunnel }) {
    const { f, set, remove, clear } = useFilters(["sector", "type", "client", "state", "tl"]);
    const params = { ...gp, tab: "9", sector: f.sector, tender_type: f.type, client: f.client, state: f.state, [TL_QUERY_PARAM]: f.tl };
    const { data, loading, error } = useDashboard(params);
    useEffect(() => { if (data?.funnel && onFunnel) onFunnel(data.funnel); }, [data, onFunnel]);

    const rawT = data?.tenders || [];
    const T = useMemo(() => f.tl ? rawT.filter(t => (t.tl_details?.assigned_tls || []).some(a => a.name === f.tl)) : rawT, [rawT, f.tl]);

    const A = data?.analytics || {};
    const mainTrend = data?.trend_overview || {};
    const hasFilters = !!(f.sector || f.type || f.client || f.state || f.tl);
    const { trend: unfilteredTrend } = useUnfilteredTrend(params.tab, gp.filter, !hasFilters);
    const trend = {
        ...mainTrend,
        sector_trend: unfilteredTrend?.sector_trend || mainTrend.sector_trend,
        type_trend: unfilteredTrend?.type_trend || mainTrend.type_trend,
        monthly_breakdown: mainTrend.monthly_breakdown || unfilteredTrend?.monthly_breakdown,
    };
    const f49 = data?.funnel?.tab4_to_tab9 || {};
    const cr = f49.conversion_rate_pct ?? 0;
    const emdData = useMemo(() => Object.entries(A.emd_analytics || {}).map(([k, c], i) => ({ key: k, count: c, color: PAL[i] })).filter(d => d.count > 0), [A]);
    const tlSrc = A?.tl_summary?.assigned_tls || f49.tl_wise_distribution?.assigned_tls || [];

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {loading && <Loader />}{error && <ErrBanner />}
            <InfoBanner title="Final → Bid Prep (4→9)" items={[
                { label: "Finalized", value: f49.total_in_tab4 ?? 0, color: C.p500 },
                { label: "In Bid Prep", value: f49.moved_to_tab9 ?? T.length, color: C.ok },
                { label: "Active in Final", value: f49.active_in_tab4 ?? 0, color: C.r500 },
                { label: "Pending in Final", value: f49.pending_in_tab4 ?? 0, color: C.warn },
                { label: "Conversion", value: `${cr}%`, color: cr >= 70 ? C.ok : C.warn },
            ]} />
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <Stat icon={<I.Check />} label="Finalized (tab 4)" value={f49.total_in_tab4 ?? 0} color={C.p500} bg={C.p50} />
                <Stat icon={<I.Doc />} label="In Bid Prep (tab 9)" value={f49.moved_to_tab9 ?? T.length} sub={`${cr}% conv.`} subUp={cr > 50} color={C.r500} bg={C.r100} />
                <Stat icon={<I.Alert />} label="Expired in Final" value={f49.expired_in_tab4 ?? 0} color={C.err} bg={C.errBg} />
                <Stat icon={<I.Clock />} label="Pending" value={f49.pending_in_tab4 ?? 0} color={C.warn} bg={C.warnBg} />
                <Stat icon={<I.Star />} label="Conversion" value={`${cr}%`} color={cr >= 70 ? C.ok : C.warn} bg={cr >= 70 ? C.okBg : C.warnBg} />
            </div>
            <ActiveChips filters={{ sector: f.sector, type: f.type, client: f.client, state: f.state, tl: f.tl ? tlDisplayLabel(f.tl, A) : null }} onRemove={remove} onClearAll={clear} />
            <STTRow analytics={A} trend={trend} activeSector={f.sector} activeType={f.type} onSector={v => set("sector", v)} onType={v => set("type", v)} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <TLCard tlSource={tlSrc} analytics={A} title="TL-wise Distribution" subtitle="From funnel data" onFilter={v => set("tl", v)} active={f.tl} />
                <Card title="EMD Distribution"><Donut data={emdData} size={120} /></Card>
            </div>
            <ClientStateRow analytics={A} activeClient={f.client} activeState={f.state} onClient={v => set("client", v)} onState={v => set("state", v)} />
            {T.length > 0 ? <TTable tenders={T} onClient={v => set("client", v)} activeClient={f.client} onState={v => set("state", v)} activeState={f.state} exportParams={params} exportFilename="final_to_bid_prep.xlsx" /> : !loading && <Empty />}
        </div>
    );
}

/* ══════════════════════════════════════════════════════
   TAB 4: BID PREP → SUBMIT
══════════════════════════════════════════════════════ */
function BidSubmitTab({ gp, onFunnel }) {
    const { f, set, remove, clear } = useFilters(["sector", "type", "client", "state", "tl"]);
    const params = { ...gp, tab: "5", sector: f.sector, tender_type: f.type, client: f.client, state: f.state, [TL_QUERY_PARAM]: f.tl };
    const { data, loading, error } = useDashboard(params);
    useEffect(() => { if (data?.funnel && onFunnel) onFunnel(data.funnel); }, [data, onFunnel]);

    const rawT = data?.tenders || [];
    const T = useMemo(() => f.tl ? rawT.filter(t => (t.tl_details?.assigned_tls || []).some(a => a.name === f.tl)) : rawT, [rawT, f.tl]);

    const A = data?.analytics || {};
    const mainTrend = data?.trend_overview || {};
    const hasFilters = !!(f.sector || f.type || f.client || f.state || f.tl);
    const { trend: unfilteredTrend } = useUnfilteredTrend(params.tab, gp.filter, !hasFilters);
    const trend = {
        ...mainTrend,
        sector_trend: unfilteredTrend?.sector_trend || mainTrend.sector_trend,
        type_trend: unfilteredTrend?.type_trend || mainTrend.type_trend,
        monthly_breakdown: mainTrend.monthly_breakdown || unfilteredTrend?.monthly_breakdown,
    };
    const f95 = data?.funnel?.tab9_to_tab5 || {};
    const cr = f95.conversion_rate_pct ?? 0;
    const emdData = useMemo(() => Object.entries(A.emd_analytics || {}).map(([k, c], i) => ({ key: k, count: c, color: PAL[i] })).filter(d => d.count > 0), [A]);
    const paData = useMemo(() => Object.entries(A.participating_as_analytics || {}).map(([k, c], i) => ({ key: k, count: c, color: PAL[i] })).filter(d => d.count > 0), [A]);
    const subTrend = (f95.submission_trend_last_6_months || []).map(d => ({ month: d.month, value: d.count }));
    const tlSrc = A?.tl_summary?.assigned_tls || f95.tl_wise_distribution?.assigned_tls || [];
    const tl = f95.timeline || {};

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {loading && <Loader />}{error && <ErrBanner />}
            <InfoBanner title="Bid Prep → Submit (9→5)" items={[
                { label: "In Bid Prep", value: f95.total_in_tab9 ?? 0, color: C.p500 },
                { label: "Submitted", value: f95.moved_to_tab5 ?? T.length, color: C.ok },
                { label: "Pending", value: f95.pending_in_tab9 ?? 0, color: C.warn },
                { label: "Expired", value: f95.expired_in_tab9 ?? 0, color: C.err },
                { label: "Submit Rate", value: `${cr}%`, color: cr >= 50 ? C.ok : C.warn },
                ...(tl.avg_days != null ? [{ label: "Avg Days (Prep→Submit)", value: `${tl.avg_days}d`, color: C.r500 }] : []),
            ]} />
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <Stat icon={<I.Doc />} label="In Bid Prep" value={f95.total_in_tab9 ?? 0} color={C.p500} bg={C.p50} />
                <Stat icon={<I.Send />} label="Submitted" value={f95.moved_to_tab5 ?? T.length} sub={`${cr}% rate`} subUp={cr > 50} color={C.ok} bg={C.okBg} />
                <Stat icon={<I.Clock />} label="Pending" value={f95.pending_in_tab9 ?? 0} color={C.warn} bg={C.warnBg} />
                <Stat icon={<I.Money />} label="Total Value" value={fmtCr(T.reduce((a, t) => a + (t.cost_amount || 0), 0))} color={C.r500} bg={C.r100} />
                <Stat icon={<I.Star />} label="Submit Rate" value={`${cr}%`} color={cr >= 50 ? C.ok : C.warn} bg={cr >= 50 ? C.okBg : C.warnBg} />
            </div>
            <ActiveChips filters={{ sector: f.sector, type: f.type, client: f.client, state: f.state, tl: f.tl ? tlDisplayLabel(f.tl, A) : null }} onRemove={remove} onClearAll={clear} />
            <STTRow analytics={A} trend={{ ...trend, monthly_breakdown: subTrend.length ? subTrend.map(d => ({ month: d.month, total_tenders: d.value })) : trend.monthly_breakdown }} activeSector={f.sector} activeType={f.type} onSector={v => set("sector", v)} onType={v => set("type", v)} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                <TLCard tlSource={tlSrc} analytics={A} title="TL-wise Distribution" onFilter={v => set("tl", v)} active={f.tl} />
                <Card title="EMD Distribution"><Donut data={emdData} size={120} /></Card>
                <Card title="Participating As"><Donut data={paData} size={120} /></Card>
            </div>
            <ClientStateRow analytics={A} activeClient={f.client} activeState={f.state} onClient={v => set("client", v)} onState={v => set("state", v)} />
            {T.length > 0 ? <TTable tenders={T} onClient={v => set("client", v)} activeClient={f.client} onState={v => set("state", v)} activeState={f.state} exportParams={params} exportFilename="bid_prep_to_submit.xlsx" /> : !loading && <Empty />}
        </div>
    );
}

/* ══════════════════════════════════════════════════════
   TAB 5: TECH OPEN
══════════════════════════════════════════════════════ */
function TechTab({ gp, onFunnel }) {
    const { f, set, remove, clear } = useFilters(["sector", "type", "client", "state", "tl"]);
    const params = { ...gp, tab: "11", sector: f.sector, tender_type: f.type, client: f.client, state: f.state, [TL_QUERY_PARAM]: f.tl };
    const { data, loading, error } = useDashboard(params);
    useEffect(() => { if (data?.funnel && onFunnel) onFunnel(data.funnel); }, [data, onFunnel]);

    const rawT = data?.tenders || [];
    const T = useMemo(() => f.tl ? rawT.filter(t => (t.tl_details?.assigned_tls || []).some(a => a.name === f.tl)) : rawT, [rawT, f.tl]);

    const A = data?.analytics || {};
    const mainTrend = data?.trend_overview || {};
    const hasFilters = !!(f.sector || f.type || f.client || f.state || f.tl);
    const { trend: unfilteredTrend } = useUnfilteredTrend(params.tab, gp.filter, !hasFilters);
    const trend = {
        ...mainTrend,
        sector_trend: unfilteredTrend?.sector_trend || mainTrend.sector_trend,
        type_trend: unfilteredTrend?.type_trend || mainTrend.type_trend,
        monthly_breakdown: mainTrend.monthly_breakdown || unfilteredTrend?.monthly_breakdown,
    };
    const f511 = data?.funnel?.tab5_to_tab11 || {};
    const cr = f511.conversion_rate_pct ?? 0;
    const avgScore = f511.tech_score_stats?.avg_tech_score ?? (T.length ? Math.round(T.reduce((a, t) => a + (parseFloat(t.tech_score) || 0), 0) / T.length) : 0);
    const emdData = useMemo(() => Object.entries(A.emd_analytics || {}).map(([k, c], i) => ({ key: k, count: c, color: PAL[i] })).filter(d => d.count > 0), [A]);
    const tlSrc = A?.tl_summary?.assigned_tls || [];
    const responsive = T.filter(t => parseInt(t.tech_score) >= 60).length;
    const scoreD = [
        { key: "0–39 (NR)", count: T.filter(t => parseInt(t.tech_score) < 40).length, color: C.err },
        { key: "40–59", count: T.filter(t => { const s = parseInt(t.tech_score); return s >= 40 && s < 60; }).length, color: C.warn },
        { key: "60–79 (R)", count: T.filter(t => { const s = parseInt(t.tech_score); return s >= 60 && s < 80; }).length, color: C.ok },
        { key: "80–100", count: T.filter(t => parseInt(t.tech_score) >= 80 && parseInt(t.tech_score) <= 100).length, color: C.p400 },
        { key: ">100", count: T.filter(t => parseInt(t.tech_score) > 100).length, color: C.l500 },
    ].filter(d => d.count > 0);
    const sectorData = useMemo(() => Object.entries(A.sector_wise_counts || {}).map(([k, c], i) => ({ key: k, count: c, color: SEC_COL[k] || PAL[i] })).sort((a, b) => b.count - a.count), [A]);

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {loading && <Loader />}{error && <ErrBanner />}
            <InfoBanner title="Tech Open (5→11)" items={[
                { label: "Submitted", value: f511.total_submitted ?? 0, color: C.p500 },
                { label: "Tech Open", value: f511.moved_to_tab11 ?? T.length, color: C.ok },
                { label: "Conversion", value: `${cr}%`, color: cr >= 5 ? C.ok : C.warn },
                { label: "Avg Score", value: avgScore || "—", color: C.r500 },
            ]} />
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <Stat icon={<I.Book />} label="Tech Open" value={f511.moved_to_tab11 ?? T.length} color={C.p500} bg={C.p50} />
                <Stat icon={<I.Check />} label="Responsive (≥60)" value={responsive} color={C.ok} bg={C.okBg} />
                <Stat icon={<I.Alert />} label="Non-Responsive" value={T.length - responsive} color={C.err} bg={C.errBg} />
                <Stat icon={<I.Star />} label="Avg Tech Score" value={avgScore || "—"} color={avgScore >= 60 ? C.ok : C.warn} bg={avgScore >= 60 ? C.okBg : C.warnBg} />
                <Stat icon={<I.Star />} label="Conversion" value={`${cr}%`} color={cr >= 5 ? C.ok : C.warn} bg={cr >= 5 ? C.okBg : C.warnBg} />
            </div>
            <ActiveChips filters={{ sector: f.sector, type: f.type, client: f.client, state: f.state, tl: f.tl ? tlDisplayLabel(f.tl, A) : null }} onRemove={remove} onClearAll={clear} />
            <STTRow analytics={A} trend={trend} activeSector={f.sector} activeType={f.type} onSector={v => set("sector", v)} onType={v => set("type", v)} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Card title="Score Distribution">
                    {scoreD.length > 0 ? <HBar data={scoreD} total={T.length} /> : <Empty msg="No tech scores yet." />}
                </Card>
                <Card title="Avg Score by Sector">
                    {sectorData.length > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            {sectorData.map((s, i) => {
                                const st = T.filter(t => t.sector === s.key);
                                const avg = st.length ? Math.round(st.reduce((a, t) => a + (parseInt(t.tech_score) || 0), 0) / st.length) : 0;
                                return <div key={s.key}>
                                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                                        <span style={{ fontSize: 10, color: C.n700 }}>{s.key}</span>
                                        <div style={{ display: "flex", gap: 7 }}><span style={{ fontSize: 10.5, fontWeight: 700, color: avg >= 60 ? C.ok : C.warn }}>{avg}</span><span style={{ fontSize: 9, color: C.n400 }}>{s.count} tenders</span></div>
                                    </div>
                                    <ProgBar value={avg} max={100} color={avg >= 60 ? C.ok : avg >= 40 ? C.warn : C.err} />
                                </div>;
                            })}
                        </div>
                    ) : <Empty msg="No data." />}
                </Card>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <TLCard tlSource={tlSrc} analytics={A} onFilter={v => set("tl", v)} active={f.tl} />
                <Card title="EMD Distribution"><Donut data={emdData} size={120} /></Card>
            </div>
            <ClientStateRow analytics={A} activeClient={f.client} activeState={f.state} onClient={v => set("client", v)} onState={v => set("state", v)} />
            {T.length > 0 ? <TTable tenders={T} extraCols={[{ header: "Score", render: t => t.tech_score || "—" }, { header: "Responsive", render: t => t.responsive_status || "—" }]} onClient={v => set("client", v)} activeClient={f.client} onState={v => set("state", v)} activeState={f.state} exportParams={params} exportFilename="tech_open.xlsx" /> : !loading && <Empty />}
        </div>
    );
}

/* ══════════════════════════════════════════════════════
   TAB 6: FINANCIAL OPEN
══════════════════════════════════════════════════════ */
function FinTab({ gp, onFunnel }) {
    const { f, set, remove, clear } = useFilters(["sector", "type", "client", "state", "tl"]);
    const [l1, setL1] = useState(null);
    const [emdF, setEmdF] = useState(null);
    const params = { ...gp, tab: "12", sector: f.sector, tender_type: f.type, client: f.client, state: f.state, l1: l1 || undefined, emd_filter: emdF || undefined, [TL_QUERY_PARAM]: f.tl };
    const { data, loading, error } = useDashboard(params);
    useEffect(() => { if (data?.funnel && onFunnel) onFunnel(data.funnel); }, [data, onFunnel]);

    const rawT = data?.tenders || [];
    const T = useMemo(() => f.tl ? rawT.filter(t => (t.tl_details?.assigned_tls || []).some(a => a.name === f.tl)) : rawT, [rawT, f.tl]);

    const A = data?.analytics || {};
    const mainTrend = data?.trend_overview || {};
    const hasFilters = !!(f.sector || f.type || f.client || f.state || f.tl || l1 || emdF);
    const { trend: unfilteredTrend } = useUnfilteredTrend(params.tab, gp.filter, !hasFilters);
    const trend = {
        ...mainTrend,
        sector_trend: unfilteredTrend?.sector_trend || mainTrend.sector_trend,
        type_trend: unfilteredTrend?.type_trend || mainTrend.type_trend,
        monthly_breakdown: mainTrend.monthly_breakdown || unfilteredTrend?.monthly_breakdown,
    };
    const f1112 = data?.funnel?.tab11_to_tab12 || {};
    const cr = f1112.conversion_rate_pct ?? 0;
    const L1T = T.filter(t => t.are_we_l1);
    const winRate = pct(L1T.length, T.length);
    const emdData = useMemo(() => Object.entries(A.emd_analytics || {}).map(([k, c], i) => ({ key: k, count: c, color: PAL[i] })).filter(d => d.count > 0), [A]);
    const tlSrc = A?.tl_summary?.assigned_tls || [];
    const sectorData = useMemo(() => Object.entries(A.sector_wise_counts || {}).map(([k, c], i) => ({ key: k, count: c, color: SEC_COL[k] || PAL[i] })).sort((a, b) => b.count - a.count), [A]);

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {loading && <Loader />}{error && <ErrBanner />}
            <InfoBanner title="Financial Open (11→12)" items={[
                { label: "Tech Open", value: f1112.total_in_tab11 ?? 0, color: C.p500 },
                { label: "Fin. Open", value: f1112.moved_to_tab12 ?? T.length, color: C.ok },
                { label: "L1 Winners", value: f1112.winner_tender_count ?? L1T.length, color: C.ok },
                { label: "Non-Winners", value: f1112.non_winner_count ?? 0, color: C.err },
                { label: "Conversion", value: `${cr}%`, color: cr >= 20 ? C.ok : C.warn },
                { label: "Win Rate", value: `${winRate}%`, color: winRate > 50 ? C.ok : C.warn },
            ]} />
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <Stat icon={<I.Money />} label="Fin. Open" value={f1112.moved_to_tab12 ?? T.length} color={C.p500} bg={C.p50} />
                <Stat icon={<I.Trophy />} label="We are L1" value={f1112.winner_tender_count ?? L1T.length} sub={`${winRate}% win rate`} subUp={winRate > 50} color={C.ok} bg={C.okBg} />
                <Stat icon={<I.Alert />} label="Not L1" value={f1112.non_winner_count ?? (T.length - L1T.length)} color={C.err} bg={C.errBg} />
                <Stat icon={<I.Star />} label="Win Rate" value={`${winRate}%`} color={winRate > 50 ? C.ok : C.warn} bg={winRate > 50 ? C.okBg : C.warnBg} />
                <Stat icon={<I.Star />} label="Conversion" value={`${cr}%`} color={cr >= 20 ? C.ok : C.warn} bg={cr >= 20 ? C.okBg : C.warnBg} />
            </div>
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", padding: "8px 12px", background: C.n150, borderRadius: 8, border: `1px solid ${C.n200}` }}>
                <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                    <span style={{ fontSize: 10, color: C.n600, fontWeight: 700 }}>L1:</span>
                    {[{ v: null, l: "All" }, { v: "true", l: "L1 Only" }, { v: "false", l: "Non-L1" }].map(o => (
                        <button key={o.l} onClick={() => setL1(o.v)}
                            style={{ padding: "3px 9px", border: `1px solid ${l1 === o.v ? C.p400 : C.n200}`, borderRadius: 6, background: l1 === o.v ? C.p500 : "transparent", color: l1 === o.v ? C.white : C.n600, fontSize: 9.5, fontWeight: l1 === o.v ? 700 : 400, cursor: "pointer", transition: "all .12s" }}>
                            {o.l}
                        </button>
                    ))}
                </div>
                <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                    <span style={{ fontSize: 10, color: C.n600, fontWeight: 700 }}>EMD:</span>
                    {[{ v: null, l: "All" }, { v: "not_required", l: "No EMD" }, { v: "required_less_1lakh", l: "< 1L" }, { v: "required_1_to_5_lakh", l: "1–5L" }].map(o => (
                        <button key={o.l} onClick={() => setEmdF(o.v)}
                            style={{ padding: "3px 9px", border: `1px solid ${emdF === o.v ? C.p400 : C.n200}`, borderRadius: 6, background: emdF === o.v ? C.p500 : "transparent", color: emdF === o.v ? C.white : C.n600, fontSize: 9.5, fontWeight: emdF === o.v ? 700 : 400, cursor: "pointer", transition: "all .12s" }}>
                            {o.l}
                        </button>
                    ))}
                </div>
            </div>
            <ActiveChips filters={{ sector: f.sector, type: f.type, client: f.client, state: f.state, tl: f.tl ? tlDisplayLabel(f.tl, A) : null }} onRemove={remove} onClearAll={clear} />
            <STTRow analytics={A} trend={trend} activeSector={f.sector} activeType={f.type} onSector={v => set("sector", v)} onType={v => set("type", v)} />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Card title="L1 by Sector">
                    {sectorData.length > 0 ? (
                        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                            {sectorData.map((s, i) => {
                                const tot = T.filter(t => t.sector === s.key).length;
                                const l1c = T.filter(t => t.sector === s.key && t.are_we_l1).length;
                                return <div key={s.key}>
                                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 2 }}>
                                        <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                            <span style={{ width: 6, height: 6, borderRadius: 1, background: s.color }} />
                                            <span style={{ fontSize: 10, color: C.n700 }}>{s.key}</span>
                                        </div>
                                        <div style={{ display: "flex", gap: 6 }}>
                                            <span style={{ fontSize: 9, color: C.n500 }}>L1:<b style={{ color: C.ok, marginLeft: 2 }}>{l1c}</b>/{tot}</span>
                                            <span style={{ fontSize: 9.5, fontWeight: 700, color: C.p500 }}>{pct(l1c, tot)}%</span>
                                        </div>
                                    </div>
                                    <ProgBar value={l1c} max={tot || 1} color={s.color} />
                                </div>;
                            })}
                        </div>
                    ) : <Empty msg="No data." />}
                </Card>
                <Card title="Win Rate Summary">
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 6, marginBottom: 10 }}>
                        {[{ label: "Total", val: T.length, col: C.p500 }, { label: "L1", val: L1T.length, col: C.ok }, { label: "Win Rate", val: `${winRate}%`, col: winRate > 50 ? C.ok : C.warn }].map(item => (
                            <div key={item.label} style={{ textAlign: "center", background: C.n100, borderRadius: 6, padding: "8px", border: `1px solid ${C.n200}` }}>
                                <div style={{ fontSize: 16, fontWeight: 800, color: item.col }}>{item.val}</div>
                                <div style={{ fontSize: 8.5, color: C.n500, marginTop: 1 }}>{item.label}</div>
                            </div>
                        ))}
                    </div>
                    <TrendLine datasets={trend.monthly_breakdown?.length ? [{ label: "Fin. Open", points: trend.monthly_breakdown.map(m => ({ month: m.month, value: m.total_tenders })) }] : []} height={90} stableKey="fin_win_rate" />
                </Card>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <TLCard tlSource={tlSrc} analytics={A} onFilter={v => set("tl", v)} active={f.tl} />
                <Card title="EMD Distribution"><Donut data={emdData} size={120} /></Card>
            </div>
            <ClientStateRow analytics={A} activeClient={f.client} activeState={f.state} onClient={v => set("client", v)} onState={v => set("state", v)} />
            {T.length > 0 ? <TTable tenders={T} extraCols={[{ header: "Score", render: t => t.tech_score || "—" }, { header: "L1?", render: t => t.are_we_l1 ? "✓" : "No" }]} onClient={v => set("client", v)} activeClient={f.client} onState={v => set("state", v)} activeState={f.state} exportParams={params} exportFilename="financial_open.xlsx" /> : !loading && <Empty />}
        </div>
    );
}

/* ══════════════════════════════════════════════════════
   TAB 7: EMD TRACKER  (NEW)
   ───────────────────────────────────────────────────────
   Unlike the six tabs above (which all walk the SAME
   bid-decision pipeline via `funnel.tab1_to_tab4` etc, and
   all pass a `tab` param), this tab hits the dashboard
   endpoint WITHOUT a `tab` param. The response's `funnel`
   key names are entirely different (`upcoming_to_created`,
   `created_to_submitted`, `submitted_to_matured`,
   `matured_to_collected`), and it ships an extra
   `stage_analytics` object keyed by EMD lifecycle stage
   (upcoming / created / submitted / bg_created /
   matured_pending / collected) instead of tab numbers.
   `analytics` here is also slimmer — sector/client/state
   counts plus an amount-aware `emd_bifurcation` (each
   bucket carries both a count AND a rupee amount) — there's
   no tender_type_counts, participating_as, partner or TL
   breakdown in this response shape, so those sections are
   simply omitted here rather than shown empty.

   Global date-range / sector / client / state filters work
   exactly the same way as every other tab (same `gp` +
   useFilters pattern) — only the EMD-stage selector below
   is unique to this tab, since it's the only place that
   concept exists.
══════════════════════════════════════════════════════ */
function EmdTab({ gp, onSummary }) {
    const { f, set, remove, clear } = useFilters(["sector", "client", "state", "emd"]);
    const [stage, setStage] = useState(null); // client-side EMD lifecycle stage filter

    const params = { ...gp, sector: f.sector, client: f.client, state: f.state, emd_filter: f.emd };
    const { data, loading, error } = useDashboard(params, false, EMD_API_PATH);

    useEffect(() => { if (data && onSummary) onSummary(data.summary?.total_records ?? data.pagination?.total_items ?? null); }, [data, onSummary]);

    const allT = data?.tenders || [];
    // Normalize a couple of field-name differences vs the bid-pipeline
    // tenders so the shared TTable/TModal components render sensibly.
    const normT = useMemo(() => allT.map(t => ({
        ...t,
        days_left: t.days_left ?? t.days_to_due,
        cost_amount: t.cost_amount ?? t.cost_of_rfp,
    })), [allT]);
    const T = useMemo(() => stage ? normT.filter(t => t._stage === stage) : normT, [normT, stage]);

    const A = data?.analytics || {};
    const SA = data?.stage_analytics || {};
    const FN = data?.funnel || {};

    const sectorData = useMemo(() => Object.entries(A.sector_wise_counts || {}).map(([k, c], i) => ({ key: k, count: c, color: SEC_COL[k] || PAL[i] })).sort((a, b) => b.count - a.count), [A]);
    const clientData = useMemo(() => Object.entries(A.client_counts || {}).filter(([k]) => k).map(([k, c], i) => ({ key: k, count: c, color: PAL[i % PAL.length] })).sort((a, b) => b.count - a.count), [A]);
    const stateData = useMemo(() => Object.entries(A.state_counts || {}).filter(([k]) => k).map(([k, c], i) => ({ key: k, count: c, color: PAL[i % PAL.length] })).sort((a, b) => b.count - a.count), [A]);
    const emdBif = useMemo(() => Object.entries(A.emd_bifurcation || {}).map(([k, v], i) => ({ key: k, count: v?.count || 0, amount: v?.amount || 0, color: PAL[i] })).filter(d => d.count > 0), [A]);

    const stageCards = EMD_STAGES.map(s => ({ ...s, data: SA[s.key] || {} }));

    const uc = FN.upcoming_to_created || {};
    const cs = FN.created_to_submitted || {};
    const sm = FN.submitted_to_matured || {};
    const mc = FN.matured_to_collected || {};

    const totalEmdAmount = SA.upcoming?.total_emd_amount ?? 0;
    const overdue = SA.matured_pending?.overdue_count ?? 0;
    const dueSoon = SA.matured_pending?.due_within_7_days_count ?? 0;

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {loading && <Loader />}{error && <ErrBanner />}

            <div style={{ background: C.warnBg, border: `1px solid ${C.warnBd}`, borderRadius: 9, padding: "7px 14px", display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{ color: C.warn }}><I.Coin /></span>
                <span style={{ fontSize: 10.5, color: C.warn, fontWeight: 600 }}>
                    This tab tracks the <b>EMD instrument lifecycle</b> (Upcoming → Created → Submitted → BG Created → Matured/Pending → Collected) — a separate money-tracking funnel from the bid-decision pipeline in the other tabs. Date range, sector, client and state filters above still apply the same way.
                </span>
            </div>

            <InfoBanner tone="amber" title="EMD Lifecycle Funnel" items={[
                { label: "Upcoming", value: uc.total_upcoming ?? 0, color: C.warn },
                { label: "→ Created", value: uc.moved_to_created ?? 0, color: C.warn },
                { label: "Created→Submit Conv.", value: `${cs.conversion_rate_pct ?? 0}%`, color: C.p500 },
                { label: "→ Submitted", value: cs.moved_to_submitted ?? 0, color: C.p500 },
                { label: "Matured / Pending", value: sm.matured_pending_collection ?? 0, color: C.err },
                { label: "Collected", value: mc.collected ?? 0, color: C.ok },
            ]} />

            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                <Stat icon={<I.Money />} label="Total EMD (Upcoming)" value={fmtCr(totalEmdAmount)} color={C.warn} bg={C.warnBg} />
                <Stat icon={<I.Alert />} label="Overdue (Matured)" value={overdue} color={C.err} bg={C.errBg} />
                <Stat icon={<I.Clock />} label="Due within 7 Days" value={dueSoon} color={C.warn} bg={C.warnBg} />
                <Stat icon={<I.Check />} label="Collected" value={mc.collected ?? 0} color={C.ok} bg={C.okBg} />
                <Stat icon={<I.Doc />} label="Pending Collection" value={mc.pending_collection ?? 0} color={C.p500} bg={C.p50} />
            </div>

            <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", padding: "8px 12px", background: C.n150, borderRadius: 8, border: `1px solid ${C.n200}` }}>
                <span style={{ fontSize: 10, color: C.n600, fontWeight: 700 }}>EMD Stage:</span>
                <button onClick={() => setStage(null)}
                    style={{ padding: "4px 10px", border: `1px solid ${stage === null ? C.warn : C.n200}`, borderRadius: 6, background: stage === null ? C.warn : "transparent", color: stage === null ? C.white : C.n600, fontSize: 9.5, fontWeight: stage === null ? 700 : 400, cursor: "pointer", transition: "all .12s" }}>
                    All ({data?.summary?.total_records ?? allT.length})
                </button>
                {stageCards.map(s => (
                    <button key={s.key} onClick={() => setStage(prev => prev === s.key ? null : s.key)}
                        style={{ padding: "4px 10px", border: `1px solid ${stage === s.key ? C.warn : C.n200}`, borderRadius: 6, background: stage === s.key ? C.warn : "transparent", color: stage === s.key ? C.white : C.n600, fontSize: 9.5, fontWeight: stage === s.key ? 700 : 400, cursor: "pointer", transition: "all .12s" }}>
                        {s.label} <span style={{ opacity: .75 }}>({s.data.total ?? 0})</span>
                    </button>
                ))}
            </div>

            <ActiveChips filters={{ sector: f.sector, client: f.client, state: f.state, emd: f.emd }} onRemove={remove} onClearAll={clear} />

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <Card title="Sector Distribution" subtitle="Click to filter">
                    <HBar data={sectorData} total={sectorData.reduce((a, d) => a + d.count, 0)} onFilter={v => set("sector", v)} active={f.sector} />
                </Card>
                <Card title="EMD Amount Bifurcation" subtitle="Click bucket to filter · amount shown per bucket">
                    <HBar data={emdBif} total={emdBif.reduce((a, d) => a + d.count, 0)} onFilter={v => set("emd", v)} active={f.emd} valueFn={d => fmtL(d.amount)} />
                </Card>
            </div>

            <ClientStateRow analytics={A} activeClient={f.client} activeState={f.state} onClient={v => set("client", v)} onState={v => set("state", v)} />

            <Card title="Stage-wise EMD Summary" subtitle="Count & total EMD amount held at each lifecycle stage · click to filter table">
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 8 }}>
                    {stageCards.map(s => (
                        <div key={s.key} onClick={() => setStage(prev => prev === s.key ? null : s.key)}
                            style={{ cursor: "pointer", padding: "9px 11px", borderRadius: 8, border: `1px solid ${stage === s.key ? C.warn : C.n200}`, background: stage === s.key ? `${C.warn}14` : C.n100, transition: "all .13s" }}>
                            <div style={{ fontSize: 9, color: C.n500, fontWeight: 600, textTransform: "uppercase", letterSpacing: .4 }}>{s.label}</div>
                            <div style={{ fontSize: 17, fontWeight: 800, color: stage === s.key ? C.warn : C.n800 }}>{s.data.total ?? 0}</div>
                            <div style={{ fontSize: 9, color: C.n500, marginTop: 2 }}>{fmtL(s.data.total_emd_amount)}</div>
                        </div>
                    ))}
                </div>
            </Card>

            {T.length > 0 ? (
                <TTable
                    tenders={T}
                    extraCols={[
                        { header: "EMD Stage", render: t => t._stage || "—" },
                        { header: "Due In", render: t => t.days_to_due != null ? `${t.days_to_due}d` : "—" },
                        { header: "BG?", render: t => t.bg_created ? "Yes" : "No" },
                    ]}
                    onClient={v => set("client", v)} activeClient={f.client}
                    onState={v => set("state", v)} activeState={f.state}
                    exportParams={params} exportFilename="emd_lifecycle.xlsx" exportEndpoint={EMD_API_PATH}
                />
            ) : !loading && <Empty msg="No EMD records for this view." />}
        </div>
    );
}

/* ══════════════════════════════════════════════════════════════
   WEEKLY BID PREP REPORT TAB
   Paste this whole block into TenderDashboard.jsx (top level, e.g.
   just above the "APP" section). It only uses things already defined
   in that file: C, PAL, SEC_COL, BASE, XLSX, Card, Stat, HBar, Donut,
   InfoBanner, Loader, Empty, I, pct, useState, useEffect, useMemo.

   WIRING (3 small edits in the App section — see bottom of this file)
══════════════════════════════════════════════════════════════ */

const WEEKLY_API_PATH = "/siteproject/weekly-bidprep-report/";

/* ---------- date helpers (pure string math, no timezone surprises) ---------- */
function wkAddDays(ymd, n) {
    const d = new Date(`${ymd}T00:00:00Z`);
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
}
function wkIsMonday(ymd) {
    return new Date(`${ymd}T00:00:00Z`).getUTCDay() === 1;
}
function wkFmtDay(ymd, opts = { day: "2-digit", month: "short" }) {
    return new Date(`${ymd}T00:00:00Z`).toLocaleDateString("en-IN", { ...opts, timeZone: "UTC" });
}
function wkIstDay(iso) {
    return iso ? new Date(iso).toLocaleDateString("en-CA", { timeZone: "Asia/Kolkata" }) : "";
}
function wkFmtDateTime(iso) {
    return iso
        ? new Date(iso).toLocaleString("en-IN", { timeZone: "Asia/Kolkata", day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit", hour12: true })
        : "—";
}
function wkCountBy(arr, fn) {
    const m = {};
    arr.forEach(x => { const k = fn(x); if (k) m[k] = (m[k] || 0) + 1; });
    return Object.entries(m).map(([key, count], i) => ({ key, count, color: PAL[i % PAL.length] })).sort((a, b) => b.count - a.count);
}
const wkPretty = s => (s ? String(s).replace(/_/g, " ") : "—");

/* ---------- data hook ---------- */
function useWeeklyReport(startDate) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    useEffect(() => {
        let dead = false;
        setLoading(true); setError(null);
        const qs = new URLSearchParams({ include_list: "true" });
        if (startDate) qs.set("start_date", startDate);
        fetch(`${BASE}${WEEKLY_API_PATH}?${qs}`, {
            signal: AbortSignal.timeout(20000),
            headers: getAuthHeaders(),
        })
            .then(async r => {
                const j = await r.json().catch(() => null);
                if (!r.ok) throw new Error(j?.error || `HTTP ${r.status}`);
                return j;
            })
            .then(d => { if (!dead) { setData(d); setLoading(false); } })
            .catch(e => { if (!dead) { setError(e.message || "Request failed"); setLoading(false); } });
        return () => { dead = true; };
    }, [startDate]);
    return { data, loading, error };
}

/* ---------- constants ---------- */
const WK_BUCKETS = [
    { key: "all_due", label: "All Due", sum: "total_due", color: C.p500 },
    { key: "submitted", label: "Submitted", sum: "submitted", color: C.ok },
    { key: "expired", label: "Expired (missed)", sum: "expired", color: C.err },
    { key: "closed_by_us", label: "Closed by us", sum: "closed_by_us", color: C.warn },
    { key: "tech_open", label: "Tech Open", sum: "tech_open", color: C.r500 },
    { key: "financial_open", label: "Financial Open", sum: "financial_open", color: C.l500 },
    { key: "lying_in_bid_prep", label: "Still in Bid Prep", sum: "lying_in_bid_prep", color: C.p400 },
];
const WK_STATUS = {
    submitted: { label: "Submitted", fg: C.ok, bg: C.okBg, bd: C.okBd },
    expired: { label: "Expired", fg: C.err, bg: C.errBg, bd: C.errBd },
    closed_by_us: { label: "Closed by us", fg: C.warn, bg: C.warnBg, bd: C.warnBd },
    lying_in_bid_prep: { label: "In Bid Prep", fg: C.p600, bg: C.p50, bd: C.p200 },
};

function WkBadge({ status }) {
    const s = WK_STATUS[status] || { label: wkPretty(status), fg: C.n600, bg: C.n150, bd: C.n200 };
    return <span style={{ background: s.bg, color: s.fg, border: `1px solid ${s.bd}`, borderRadius: 4, padding: "2px 6px", fontSize: 8.5, fontWeight: 700, whiteSpace: "nowrap" }}>{s.label}</span>;
}

/* the "why" for closed tenders */
function wkReason(t) {
    if (t.is_rejected) {
        const r = t.rejected_reason === "other" ? t.rejected_reason_other : (t.rejected_reason || t.rejected_reason_other);
        return `Rejected${r ? `: ${wkPretty(r)}` : ""}`;
    }
    if (t.is_cancelled) return `Cancelled${t.reason_remark_cancellation ? `: ${t.reason_remark_cancellation}` : ""}`;
    if (t.week_status === "expired") {
        if (!t.assigned_to?.length) return "Expired — nobody assigned";
        return `Expired — stuck at ${t.current_tab}`;
    }
    return "";
}

/* ---------- table ---------- */
function WeeklyTable({ rows, filename }) {
    const [page, setPage] = useState(1);
    const [q, setQ] = useState("");
    const PP = 15;
    const filtered = useMemo(() => {
        const s = q.trim().toLowerCase();
        if (!s) return rows;
        return rows.filter(t => [t.tender_id, t.company_project_code_bd, t.tender_name, t.client, t.state, t.sector, t.lead_partner, (t.assigned_to || []).join(" ")]
            .some(v => String(v || "").toLowerCase().includes(s)));
    }, [rows, q]);
    useEffect(() => setPage(1), [rows, q]);
    const pages = Math.max(1, Math.ceil(filtered.length / PP));
    const view = filtered.slice((page - 1) * PP, page * PP);

    function exportXlsx() {
        const out = filtered.map(t => ({
            "Tender ID": t.tender_id, "Project Code": t.company_project_code_bd, "Tender Name": t.tender_name,
            "Client": t.client, "Sector": t.sector, "State": t.state, "Type": t.tender_type,
            "Submission Date (IST)": wkFmtDateTime(t.submission_date), "Lead Partner": t.lead_partner || "",
            "Bid Prepared By": t.bid_prepare_by || "", "Assigned To": (t.assigned_to || []).join(", "),
            "Current Tab": t.current_tab, "Week Status": WK_STATUS[t.week_status]?.label || t.week_status, "Remark": wkReason(t),
        }));
        const ws = XLSX.utils.json_to_sheet(out);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Weekly Bid Prep");
        XLSX.writeFile(wb, filename);
    }

    const th = { padding: "6px 9px", textAlign: "left", fontWeight: 600, color: C.n600, borderBottom: `1px solid ${C.n200}`, whiteSpace: "nowrap", fontSize: 9 };
    const td = { padding: "5px 9px", fontSize: 9, color: C.n700 };

    return (
        <div style={{ background: C.white, borderRadius: 10, border: `1px solid ${C.n200}`, boxShadow: C.sh1, overflow: "hidden" }}>
            <div style={{ padding: "9px 14px", borderBottom: `1px solid ${C.n200}`, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 11.5, fontWeight: 700, color: C.n800 }}>Tender List <span style={{ fontSize: 10, fontWeight: 400, color: C.n500 }}>({filtered.length})</span></span>
                <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <div style={{ position: "relative" }}>
                        <span style={{ position: "absolute", left: 7, top: "50%", transform: "translateY(-50%)", color: C.n400 }}><I.Search /></span>
                        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search ID, name, client, TL…"
                            style={{ padding: "4px 8px 4px 24px", border: `1px solid ${C.n300}`, borderRadius: 6, fontSize: 9.5, width: 210, outline: "none", color: C.n700 }} />
                    </div>
                    <button onClick={exportXlsx} disabled={!filtered.length}
                        style={{ display: "flex", alignItems: "center", gap: 3, padding: "4px 9px", background: C.p500, color: C.white, border: "none", borderRadius: 5, fontSize: 9.5, fontWeight: 600, cursor: "pointer" }}>
                        <I.Export />Export
                    </button>
                </div>
            </div>
            <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                        <tr style={{ background: C.n150 }}>
                            {["#", "Tender ID", "Code", "Name", "Client", "Sector", "State", "Type", "Submission (IST)", "Lead", "Assigned", "Current Tab", "Status", "Remark"].map(h => <th key={h} style={th}>{h}</th>)}
                        </tr>
                    </thead>
                    <tbody>
                        {view.map((t, i) => (
                            <tr key={t.id} style={{ borderBottom: `1px solid ${C.n200}`, background: t.week_status === "expired" ? C.errBg : i % 2 === 0 ? C.n100 : C.white }}>
                                <td style={{ ...td, color: C.n400, fontSize: 8.5 }}>{(page - 1) * PP + i + 1}</td>
                                <td style={{ ...td, fontFamily: "monospace", fontSize: 8, color: C.p400, fontWeight: 700, whiteSpace: "nowrap" }}>{t.tender_id}</td>
                                <td style={{ ...td, fontFamily: "monospace", fontSize: 8, whiteSpace: "nowrap" }}>{t.company_project_code_bd}</td>
                                <td style={{ ...td, maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: C.n800 }} title={t.tender_name}>{t.tender_name}</td>
                                <td style={{ ...td, fontWeight: 600, whiteSpace: "nowrap" }}>{wkPretty(t.client)}</td>
                                <td style={td}><span style={{ background: C.p50, color: SEC_COL[t.sector] || C.p500, borderRadius: 4, padding: "2px 5px", fontSize: 8, fontWeight: 700 }}>{t.sector}</span></td>
                                <td style={{ ...td, whiteSpace: "nowrap" }}>{t.state}</td>
                                <td style={{ ...td, whiteSpace: "nowrap" }}>{wkPretty(t.tender_type)}</td>
                                <td style={{ ...td, whiteSpace: "nowrap" }}>{wkFmtDateTime(t.submission_date)}</td>
                                <td style={{ ...td, whiteSpace: "nowrap" }}>{t.lead_partner || "—"}</td>
                                <td style={{ ...td, whiteSpace: "nowrap", color: (t.assigned_to || []).length ? C.n700 : C.err }}>{(t.assigned_to || []).join(", ") || "Unassigned"}</td>
                                <td style={td}><span style={{ background: C.n150, border: `1px solid ${C.n200}`, borderRadius: 4, padding: "2px 6px", fontSize: 8.5, fontWeight: 600, color: C.n700, whiteSpace: "nowrap" }}>{t.current_tab}</span></td>
                                <td style={td}><WkBadge status={t.week_status} /></td>
                                <td style={{ ...td, maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: C.n600 }} title={wkReason(t)}>{wkReason(t) || "—"}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {view.length === 0 && <div style={{ padding: 20, textAlign: "center", color: C.n400, fontSize: 11 }}>No tenders in this view</div>}
            </div>
            {pages > 1 && (
                <div style={{ padding: "7px 14px", borderTop: `1px solid ${C.n200}`, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <span style={{ fontSize: 9.5, color: C.n500 }}>Page {page} / {pages}</span>
                    <div style={{ display: "flex", gap: 3 }}>
                        <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1} style={{ padding: "3px 7px", border: `1px solid ${C.n200}`, borderRadius: 5, background: page === 1 ? C.n150 : C.white, color: page === 1 ? C.n400 : C.n700, cursor: page === 1 ? "default" : "pointer", fontSize: 10 }}>‹</button>
                        <button onClick={() => setPage(p => Math.min(pages, p + 1))} disabled={page === pages} style={{ padding: "3px 7px", border: `1px solid ${C.n200}`, borderRadius: 5, background: page === pages ? C.n150 : C.white, color: page === pages ? C.n400 : C.n700, cursor: page === pages ? "default" : "pointer", fontSize: 10 }}>›</button>
                    </div>
                </div>
            )}
        </div>
    );
}

/* ---------- main tab ---------- */
function WeeklyBidPrepTab({ onSummary }) {
    const [startDate, setStartDate] = useState(null);   // null => backend default (last completed week)
    const [bucket, setBucket] = useState("all_due");
    const { data, loading, error } = useWeeklyReport(startDate);

    useEffect(() => { if (data?.summary && onSummary) onSummary(data.summary.total_due); }, [data, onSummary]);

    const S = data?.summary || {};
    const lists = data?.tenders || {};
    const all = lists.all_due || [];
    const weekStart = data?.week_start;
    const weekEnd = data?.week_end;                       // exclusive Monday
    const weekLastDay = weekEnd ? wkAddDays(weekEnd, -1) : null;

    const statusDonut = [
        { key: "Submitted", count: S.submitted || 0, color: C.ok },
        { key: "Expired", count: S.expired || 0, color: C.err },
        { key: "Closed by us", count: S.closed_by_us || 0, color: C.warn },
        { key: "Still in Bid Prep", count: S.lying_in_bid_prep || 0, color: C.p400 },
    ].filter(d => d.count > 0);

    const clientData = useMemo(() => wkCountBy(all, t => wkPretty(t.client)), [all]);
    const sectorData = useMemo(() => wkCountBy(all, t => t.sector).map((d, i) => ({ ...d, color: SEC_COL[d.key] || PAL[i % PAL.length] })), [all]);
    const leadData = useMemo(() => wkCountBy(all, t => t.lead_partner || "Unspecified"), [all]);
    const tlData = useMemo(() => {
        const m = {};
        all.forEach(t => (t.assigned_to || []).forEach(c => { m[c] = (m[c] || 0) + 1; }));
        return Object.entries(m).map(([key, count], i) => ({ key, count, color: PAL[i % PAL.length] })).sort((a, b) => b.count - a.count);
    }, [all]);
    const reasonData = useMemo(() => wkCountBy(all.filter(t => t.week_status === "closed_by_us"), t => wkReason(t)), [all]);

    // day-wise: one row per calendar day of the window
    const dayRows = useMemo(() => {
        if (!weekStart) return [];
        return Array.from({ length: 7 }, (_, i) => {
            const day = wkAddDays(weekStart, i);
            const d = all.filter(t => wkIstDay(t.submission_date) === day);
            const c = st => d.filter(t => t.week_status === st).length;
            return { day, total: d.length, submitted: c("submitted"), expired: c("expired"), closed: c("closed_by_us"), prep: c("lying_in_bid_prep") };
        });
    }, [all, weekStart]);
    const maxDay = Math.max(...dayRows.map(r => r.total), 1);

    const submitRate = S.total_due ? pct(S.submitted, S.total_due) : 0;
    const missedRate = S.total_due ? pct(S.expired, S.total_due) : 0;
    const unassignedMissed = (lists.expired || []).filter(t => !(t.assigned_to || []).length).length;

    const navBtn = { padding: "5px 10px", border: `1px solid ${C.n200}`, borderRadius: 6, background: C.white, color: C.n700, fontSize: 10, fontWeight: 600, cursor: "pointer" };
    const rows = lists[bucket] || [];
    const activeBucket = WK_BUCKETS.find(b => b.key === bucket);

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            {loading && <Loader />}
            {error && (
                <div style={{ background: C.errBg, border: `1px solid ${C.errBd}`, borderRadius: 8, padding: "9px 14px", fontSize: 11, color: C.err, display: "flex", alignItems: "center", gap: 6 }}>
                    <I.Alert />{error}
                </div>
            )}

            {/* ── week picker ── */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", padding: "9px 14px", background: C.white, border: `1px solid ${C.n200}`, borderRadius: 10, boxShadow: C.sh1 }}>
                <button style={navBtn} disabled={!weekStart} onClick={() => setStartDate(wkAddDays(weekStart, -7))}>‹ Prev week</button>
                <div style={{ textAlign: "center", minWidth: 190 }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: C.n900 }}>
                        {weekStart ? `${wkFmtDay(weekStart)} – ${wkFmtDay(weekLastDay, { day: "2-digit", month: "short", year: "numeric" })}` : "Loading…"}
                    </div>
                    <div style={{ fontSize: 9, color: C.n500 }}>Mon 00:00 → Sun 23:59 · by submission date</div>
                </div>
                <button style={navBtn} disabled={!weekStart} onClick={() => setStartDate(wkAddDays(weekStart, 7))}>Next week ›</button>
                <span style={{ width: 1, height: 22, background: C.n200, margin: "0 4px" }} />
                <label style={{ fontSize: 10, color: C.n600, fontWeight: 600, display: "flex", alignItems: "center", gap: 5 }}>
                    Jump to Monday:
                    <input type="date" value={weekStart || ""} onChange={e => {
                        const v = e.target.value;
                        if (!v) return;
                        if (!wkIsMonday(v)) { alert("Please pick a Monday — weeks start on Monday."); return; }
                        setStartDate(v);
                    }} style={{ padding: "3px 6px", border: `1px solid ${C.n300}`, borderRadius: 6, fontSize: 10, color: C.n700 }} />
                </label>
                <button style={{ ...navBtn, color: C.p600, borderColor: C.p200, background: C.p50 }} onClick={() => setStartDate(null)}>Last completed week</button>
                {data && (
                    <span style={{ marginLeft: "auto", fontSize: 9.5, fontWeight: 700, padding: "3px 9px", borderRadius: 20, background: data.is_week_completed ? C.okBg : C.warnBg, color: data.is_week_completed ? C.ok : C.warn, border: `1px solid ${data.is_week_completed ? C.okBd : C.warnBd}` }}>
                        {data.is_week_completed ? "Week completed" : "Week in progress"}
                    </span>
                )}
            </div>

            {data && S.total_due === 0 && !loading && <Empty msg="No bid-prep tenders were due in this week." />}

            {data && S.total_due > 0 && (
                <>
                    <InfoBanner title="Weekly Snapshot" items={[
                        { label: "Due this week", value: S.total_due, color: C.p500 },
                        { label: "Submitted", value: `${S.submitted} (${submitRate}%)`, color: C.ok },
                        { label: "Missed / Expired", value: `${S.expired} (${missedRate}%)`, color: S.expired ? C.err : C.ok },
                        { label: "Closed by us", value: S.closed_by_us, color: C.warn },
                        ...(unassignedMissed ? [{ label: "Missed with no assignee", value: unassignedMissed, color: C.err }] : []),
                    ]} />

                    {/* ── stat cards (click = jump to that list) ── */}
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                        <Stat icon={<I.Doc />} label="Total Due" value={S.total_due} color={C.p500} bg={C.p50} />
                        <Stat icon={<I.Send />} label="Submitted" value={S.submitted} sub={`${submitRate}% of due`} subUp={submitRate >= 50} color={C.ok} bg={C.okBg} />
                        <Stat icon={<I.Alert />} label="Expired (missed)" value={S.expired} color={C.err} bg={C.errBg} />
                        <Stat icon={<I.X />} label="Closed by us" value={S.closed_by_us} color={C.warn} bg={C.warnBg} />
                        <Stat icon={<I.Book />} label="Tech Open" value={S.tech_open} color={C.r500} bg={C.r100} />
                        <Stat icon={<I.Money />} label="Financial Open" value={S.financial_open} color={C.l500} bg={C.p50} />
                        <Stat icon={<I.Clock />} label="Still in Bid Prep" value={S.lying_in_bid_prep} color={C.p600} bg={C.p50} />
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 12 }}>
                        <Card title="Outcome Split" subtitle="what happened to the tenders due this week">
                            <Donut data={statusDonut} size={150} />
                        </Card>
                        <Card title="Day-wise Deadlines" subtitle="tenders due per day, split by outcome">
                            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                {dayRows.map(r => (
                                    <div key={r.day} style={{ display: "grid", gridTemplateColumns: "78px 1fr 28px", alignItems: "center", gap: 8 }}>
                                        <span style={{ fontSize: 9.5, color: C.n700, fontWeight: 600 }}>{wkFmtDay(r.day, { weekday: "short", day: "2-digit", month: "short" })}</span>
                                        <div style={{ display: "flex", height: 14, background: C.n150, borderRadius: 4, overflow: "hidden", width: `${Math.max((r.total / maxDay) * 100, r.total ? 6 : 0)}%`, minWidth: r.total ? 20 : 0 }}>
                                            {[["submitted", C.ok], ["expired", C.err], ["closed", C.warn], ["prep", C.p400]].map(([k, col]) => r[k] > 0 && (
                                                <div key={k} title={`${k}: ${r[k]}`} style={{ flex: r[k], background: col, color: C.white, fontSize: 8, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center" }}>{r[k]}</div>
                                            ))}
                                        </div>
                                        <span style={{ fontSize: 10.5, fontWeight: 800, color: r.total ? C.n800 : C.n300, textAlign: "right" }}>{r.total}</span>
                                    </div>
                                ))}
                            </div>
                            <div style={{ display: "flex", gap: 10, marginTop: 9, flexWrap: "wrap" }}>
                                {[["Submitted", C.ok], ["Expired", C.err], ["Closed by us", C.warn], ["In Bid Prep", C.p400]].map(([l, col]) => (
                                    <span key={l} style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 9, color: C.n600 }}><span style={{ width: 8, height: 8, borderRadius: 2, background: col }} />{l}</span>
                                ))}
                            </div>
                        </Card>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 }}>
                        <Card title="By Client"><HBar data={clientData} total={all.length} compact /></Card>
                        <Card title="By Sector"><HBar data={sectorData} total={all.length} compact /></Card>
                        <Card title="By Lead Partner"><HBar data={leadData} total={all.length} compact /></Card>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                        <Card title="Workload by Assignee" subtitle="tenders each person was assigned (a tender can have several)"><HBar data={tlData} total={all.length} compact /></Card>
                        <Card title="Why tenders were closed" subtitle="cancelled / rejected reasons">
                            {reasonData.length ? <HBar data={reasonData} total={S.closed_by_us} compact /> : <Empty msg="Nothing closed this week." />}
                        </Card>
                    </div>

                    {/* ── bucket switcher + table ── */}
                    <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center", padding: "8px 12px", background: C.n150, borderRadius: 8, border: `1px solid ${C.n200}` }}>
                        <span style={{ fontSize: 10, color: C.n600, fontWeight: 700 }}>Show:</span>
                        {WK_BUCKETS.map(b => {
                            const act = bucket === b.key;
                            return (
                                <button key={b.key} onClick={() => setBucket(b.key)}
                                    style={{ padding: "4px 10px", border: `1px solid ${act ? b.color : C.n200}`, borderRadius: 6, background: act ? b.color : "transparent", color: act ? C.white : C.n600, fontSize: 9.5, fontWeight: act ? 700 : 400, cursor: "pointer", transition: "all .12s" }}>
                                    {b.label} <span style={{ opacity: .8 }}>({S[b.sum] ?? 0})</span>
                                </button>
                            );
                        })}
                    </div>
                    <WeeklyTable rows={rows} filename={`weekly_bidprep_${weekStart}_${activeBucket?.key}.xlsx`} />
                </>
            )}
        </div>
    );
}

/* ══════════════ WIRING — 3 small edits in the App section ══════════════

1) TABS array — add this entry (e.g. after financial_result_analytics):
   { key: "weekly_bidprep", label: "Weekly Bid Prep Report",
     info: "Week-wise report (Mon–Sun) of tenders that were in bid prep and due that week — how many got submitted, expired, were closed by us, or reached Tech / Financial open. Has its own week picker; the global date filter does not apply." },

2) In TenderDashboard():
   a) add state:      const [weeklyTotal, setWeeklyTotal] = useState(null);
   b) add to tabCount: weekly_bidprep: weeklyTotal,

3) In the render block, add:
   {tab === "weekly_bidprep" && <WeeklyBidPrepTab onSummary={setWeeklyTotal} />}
*/

/* ══════════════════════════════════════════════════════
   APP
   FIX #1: removed the separate "root" useDashboard call
   that was only there to feed the PipelineStrip/tab-count
   numbers. The funnel object is identical no matter which
   `tab` param is requested (every tab response already
   includes the full funnel across ALL stages), so we now
   simply lift whichever tab's funnel arrives most recently
   via the onFunnel callback below. That removes one full
   duplicate network call on every load and every tab switch.

   The EMD Tracker tab is the one exception: it queries the
   endpoint WITHOUT a `tab` param, so its funnel/summary
   numbers are structurally different and are NOT mixed
   into the shared `funnel` state used by the bid-pipeline
   strip/tab badges above. It reports its own total via
   `onEmdSummary` instead, purely for its own tab badge.
══════════════════════════════════════════════════════ */
export default function TenderDashboard() {
    const [tab, setTab] = useState("new");
    const [dateFilter, setDateFilter] = useState("1_month");
    const [search, setSearch] = useState("");
    const [searchIn, setSearchIn] = useState("");
    const [funnel, setFunnel] = useState(null);
    const [emdTotal, setEmdTotal] = useState(null);
    const [weeklyTotal, setWeeklyTotal] = useState(null);
    const gp = useMemo(() => ({ filter: dateFilter, search: search || undefined }), [dateFilter, search]);

    const f14 = funnel?.tab1_to_tab4 || {}; const f49 = funnel?.tab4_to_tab9 || {};
    const f95 = funnel?.tab9_to_tab5 || {}; const f511 = funnel?.tab5_to_tab11 || {}; const f1112 = funnel?.tab11_to_tab12 || {};
    // const tabCount = { new: f14.total_eligible, new_final: f14.moved_to_tab4, final_bid: f49.moved_to_tab9, bid_submit: f95.moved_to_tab5, tech_open: f511.moved_to_tab11, fin_open: f1112.moved_to_tab12, emd: emdTotal };
    const tabCount = { new: f14.total_eligible, new_final: f14.moved_to_tab4, final_bid: f49.moved_to_tab9, bid_submit: f95.moved_to_tab5, tech_open: f511.moved_to_tab11, technical_result_analytics: f511.moved_to_tab11, fin_open: f1112.moved_to_tab12, financial_result_analytics: f1112.moved_to_tab12, emd: emdTotal, weekly_bidprep: weeklyTotal };
    const DATE_OPTS = [{ k: "today", l: "Today" }, { k: "1_week", l: "1 Week" }, { k: "1_month", l: "1 Month" }, { k: "all", l: "All Time" }];

    return (
        <div style={{ minHeight: "100vh", background: "#f0f4f9", fontFamily: "'DM Sans',system-ui,sans-serif", color: C.n900 }}>
            <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@400;500;600;700;800&display=swap');
        *{box-sizing:border-box;margin:0;padding:0}
        ::-webkit-scrollbar{width:5px;height:5px}::-webkit-scrollbar-track{background:#f0f4f9}::-webkit-scrollbar-thumb{background:#93c5f4;border-radius:3px}
        @keyframes ls{0%{background-position:200% 0}100%{background-position:-200% 0}}
        @keyframes fadeUp{from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}}
        .fu{animation:fadeUp .18s ease}
        button,input{font-family:inherit}
      `}</style>
            <div style={{ background: C.white, borderBottom: `1px solid ${C.n200}`, padding: "10px 22px", display: "flex", alignItems: "center", justifyContent: "space-between", boxShadow: C.sh1, position: "sticky", top: 0, zIndex: 300 }}>
                <div>
                    <div style={{ fontSize: 16, fontWeight: 800, color: C.n900, letterSpacing: -.5 }}>Tender Dashboard</div>
                    {/* <div style={{ fontSize: 9, color: C.n500, marginTop: 1 }}>trend=true · default 1 month · {BASE}</div> */}
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                    {/* <div style={{ position: "relative" }}>
                        <span style={{ position: "absolute", left: 8, top: "50%", transform: "translateY(-50%)", color: C.n400 }}><I.Search /></span>
                        <input value={searchIn} onChange={e => setSearchIn(e.target.value)}
                            onKeyDown={e => e.key === "Enter" && setSearch(searchIn)}
                            placeholder="Search… press Enter"
                            style={{ padding: "5px 10px 5px 27px", border: `1px solid ${C.n300}`, borderRadius: 7, fontSize: 10, color: C.n700, background: C.white, width: 185, outline: "none" }} 
                            />
                    </div> */}
                    {search && <button onClick={() => { setSearch(""); setSearchIn(""); }} style={{ fontSize: 9.5, color: C.err, background: C.errBg, border: `1px solid ${C.errBd}`, borderRadius: 5, padding: "3px 8px", cursor: "pointer" }}>✕ Clear</button>}
                    <div style={{ display: "flex", background: C.n150, borderRadius: 7, border: `1px solid ${C.n200}`, overflow: "hidden" }}>
                        {DATE_OPTS.map(o => (
                            <button key={o.k} onClick={() => setDateFilter(o.k)}
                                style={{ padding: "5px 11px", border: "none", borderRight: `1px solid ${C.n200}`, background: dateFilter === o.k ? C.p500 : "transparent", color: dateFilter === o.k ? C.white : C.n600, fontSize: 10, fontWeight: dateFilter === o.k ? 700 : 400, cursor: "pointer", transition: "all .12s", whiteSpace: "nowrap" }}>
                                {o.l}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
            <PipelineStrip funnel={funnel} activeTab={tab} setTab={setTab} />
            <div style={{ background: C.white, borderBottom: `1px solid ${C.n200}`, padding: "0 22px", display: "flex", alignItems: "center", position: "sticky", top: 48, zIndex: 200, overflowX: "auto", boxShadow: "0 1px 4px rgba(17,118,211,.05)" }}>
                {TABS.map(t => {
                    const act = tab === t.key;
                    const isEmd = !!t.accent;
                    const accentSolid = isEmd ? C.warn : C.p500;
                    const accentLine = isEmd ? C.warn : C.p400;
                    return (
                        <div key={t.key} style={{ display: "flex", alignItems: "center" }}>
                            {isEmd && <span style={{ width: 1, height: 22, background: C.n300, margin: "0 6px", flexShrink: 0 }} />}
                            <button onClick={() => setTab(t.key)}
                                style={{
                                    padding: "9px 13px",
                                    border: "none",
                                    borderBottom: `2px solid ${act ? accentLine : "transparent"}`,
                                    background: isEmd ? (act ? C.warnBg : "rgba(255,184,0,0.06)") : "transparent",
                                    color: act ? accentSolid : (isEmd ? C.warn : C.n500),
                                    fontSize: 10.5,
                                    fontWeight: act || isEmd ? 700 : 400,
                                    cursor: "pointer",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 5,
                                    transition: "all .13s",
                                    whiteSpace: "nowrap",
                                    flexShrink: 0,
                                    borderRadius: isEmd ? "6px 6px 0 0" : 0,
                                }}>
                                {isEmd && <I.Coin />}
                                {t.label}
                                <span style={{
                                    background: act ? accentSolid : (isEmd ? C.warnBd : C.n200),
                                    color: act ? C.white : (isEmd ? C.warn : C.n500),
                                    borderRadius: 9, padding: "1px 6px", fontSize: 8.5, fontWeight: 700
                                }}>
                                    {tabCount[t.key] ?? "—"}
                                </span>
                            </button>
                        </div>
                    );
                })}
            </div>
            {TABS.find(t => t.key === tab) && (
                <div style={{ background: tab === "emd" ? "#fffaf0" : `${C.p50}`, borderBottom: `1px solid ${tab === "emd" ? C.warnBd : C.p100}`, padding: "6px 22px", display: "flex", alignItems: "center", gap: 6 }}>
                    <span style={{ color: tab === "emd" ? C.warn : C.p600 }}><I.Info /></span>
                    <span style={{ fontSize: 10, color: C.n600 }}><b style={{ color: C.n800 }}>{TABS.find(t => t.key === tab)?.label}:</b> {TABS.find(t => t.key === tab)?.info}</span>
                </div>
            )}
            <div style={{ padding: "14px 22px", maxWidth: 1700, margin: "0 auto" }} className="fu" key={tab + dateFilter + search}>
                {tab === "weekly_bidprep" && <WeeklyBidPrepTab onSummary={setWeeklyTotal} />}
                {tab === "period_report" && <TenderPeriodReport />}
                {tab === "new" && <NewTab gp={gp} onFunnel={setFunnel} />}
                {tab === "new_final" && <NewFinalTab gp={gp} onFunnel={setFunnel} />}
                {tab === "final_bid" && <FinalBidTab gp={gp} onFunnel={setFunnel} />}
                {tab === "bid_submit" && <BidSubmitTab gp={gp} onFunnel={setFunnel} />}
                {tab === "tech_open" && <TechTab gp={gp} onFunnel={setFunnel} />}
                {tab === "fin_open" && <FinTab gp={gp} onFunnel={setFunnel} />}
                {tab === "emd" && <EmdTab gp={gp} onSummary={setEmdTotal} />}
                {tab === "technical_result_analytics" && <TechResultAnalyticsTab gp={gp} onFunnel={setFunnel} />}
                {tab === "financial_result_analytics" && <FinancialResultAnalyticsTab gp={gp} onFunnel={setFunnel} />}

            </div>
        </div>
    );
}

